import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostListener,
  NgZone,
  AfterViewInit,
  OnInit,
  QueryList,
  signal,
  Signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DashboardService } from '../../services/dashboard.service';
import { HttpParams } from '@angular/common/http';
import { publicMethods } from '../../globals';
import { CommunicationService } from '../../services/communication.service';
import { DataSharingService } from '../../services/data-sharing.service';
import { TaggingService } from '../../services/tagging.service';
import { ActivatedRoute } from '@angular/router';
import { CustomColumnService } from '../../services/custom-column.service';
import { AlertsService } from '../../services/alerts.service';
import { ZintPropensityScoreService } from '../../services/zint-propensity-score.service';
import { ResultsFormatEditorService } from '../../services/results-format-editor.service';
import { TerritoryService } from '../../services/territory.service';
import { tangibleAssets, intangibleAssets } from '../../classes/asset-classes';
import {
  heatScore,
  wages,
  fleet,
  numberOfActiveTrademarks,
  esosNotifications,
  epcEnergy,
} from '../../classes/zint-slider-configs';
import { IZintSliderElementUpdate } from '../../classes/types';
import { ZintNgxSliderComponent } from '../zint-ngx-slider/zint-ngx-slider.component';
import {
  collapseCardToParamsMappings,
  filterIdToKeywordsMappings,
  magicFilterParams,
} from '../../classes/dashboard-filter-keywords';
import { MatDrawer } from '@angular/material/sidenav';
import { fromEvent, Observable, of } from 'rxjs';
import { debounceTime, delay, tap } from 'rxjs/operators';
import { extendedCountriesList } from '../../classes/company';
import { NEWS_EVENT_CATEGORY_GROUPS } from '../../classes/news-event-categories';
import { PushMessagesService } from '../../services/push-messages.service';
import { CRMIntegrationsService } from '../../services/crm-integrations.service';
import { MessageService } from '../../services/message.service';
import { AccountService } from '../../services/account.service';
import { GetInsightsComponent } from '../html-snippet-components/get-insights/get-insights.component';

declare var $: any;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, AfterViewInit {
  constructor(
    private dashboardService: DashboardService,
    private territoryService: TerritoryService,
    private communicationService: CommunicationService,
    public pubMethods: publicMethods,
    private dataStore: DataSharingService,
    private taggingService: TaggingService,
    private customColumnService: CustomColumnService,
    private alertsService: AlertsService,
    private zintPropensityScoreService: ZintPropensityScoreService,
    private resultsFormatEditorService: ResultsFormatEditorService,
    public pushMessagesService: PushMessagesService,
    private crmService: CRMIntegrationsService,
    private accountService: AccountService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    effect(() => {
      if (this.allEndpointsLoaded() === true) {
        this.initialiseBasedUponGetParamsIfAllLoaded();
        this.confidenceSliders?.changes.subscribe(() => {
          this.applySavedConfidenceToSliders();
        });

        if (this.pushMessagesService.showPopupPushMsg()) {
          $('#gr-popup-modal').modal({
            backdrop: 'static',
          });
          $('#gr-popup-modal').on('hide.bs.modal', _ =>
            this.pushMessagesService.showPopupPushMsg.set(false)
          );
        }
      }
    });

    effect(() => {
      const isSmall = this.isScreenTooSmall();
      this.ngZone.run(() => {
        if (isSmall) {
          this.toggleSidebarHeight(false);
        } else {
          this.toggleSidebarHeight(true);
        }
      });
      this.cdr.detectChanges();
    });

    effect(() => {
      if (!this.profilePreviewIsOpen()) {
        this.dataStore.companyNumberFromParent.set('');
      }
    });
  }

  urlQueryParams: Record<string, any> = {};
  companies: Array<any> = [];
  showDownloadModal = false;
  numberUBOsRange: Array<any>;
  countriesOfUBOsOptions: Array<any> = [];
  countriesOfOriginOptions: Array<any>;
  countriesOfOfficesOptions: Array<any>;
  countriesOfGeographicalRevenueOptions: Array<any>;
  territoriesOfGeographicalRevenueOptions: Array<any>;
  geographicalRevenueOptions: Array<any>;
  ukRegions: Array<any>;
  companyCategoryOptions: Array<any>;
  // selected and excluded properties will be set and used to build selectedSICCodes and excludedSICCodes arrays
  companySICCodeOptions: Array<any>;
  // SIC-code selections restored from a saved search / shared URL can arrive
  // before companySICCodeOptions has loaded. Stash them and re-apply once the
  // options land in updateUIComponents() (ZINT-ANGULARAPP-2AY).
  pendingSelectedSICCodes: Array<string> = [];
  pendingExcludedSICCodes: Array<string> = [];
  companyStatusOptions: Array<any> = [];
  employeeRangeFigures: Array<any>;
  headcountGrowthPercentageRange: Array<number>;
  functionalDepartmentOptions: Array<any>;
  companyAgeRange: Array<any>;
  numberOfKnownOfficeLocationsRange: Array<any>;
  LEIStatusOptions: Array<any>;
  importExportCategories: Array<any> = [];
  importingExportingBothOptions: Array<any>;
  importExportMonthRange: Array<any>;
  importExportFrequencyRange: Array<any>;
  importExportFrequencyMonthCountRange: Array<any>;
  webTechnologyOptions: Array<any> = [];
  webTrafficOptions: Array<any>;
  averageDirectorAgeRange: Array<any>;
  numberOfDirectorsRange: Array<any>;
  numberOfPSCsRange: Array<any>;
  recentDirectorshipMonthRange: Array<any>;
  nationalitiesOfDirectorsOptions: Array<any> = [];
  nationalitiesOfPSCsOptions: Array<any> = [];
  daysUntilFinancialYearEndLimits: Array<any>;
  financialYearEndsOptions: Array<any>;
  daysUntilAnniversaryLimits: Array<any>;
  hiringDayLimits: Array<any>;
  numberOfRolesHiringForLimits: Array<any>;
  numberOfRolesHiringForDayLimits: Array<any>;
  investmentRaisedOptions: Array<any>;
  shareFilingEventWithinDaysOptions: Array<any>;
  shareFilingCapitalValueOptions: Array<any>;
  accountsDueInRange: Array<any>;
  latestAccountsSubmittedRange: Array<any>;
  accountGroupOptions: Array<any>;
  accountAuditOptions: Array<any>;
  grantRangeOptions: Array<any>;
  grantMonthsUntilProjectEndRange: Array<any>;
  mileRadiusRegisteredAddressRange: Array<any>;
  mileRadiusEventsRange: Array<any>;
  eventDaysRange: Array<any>;
  addressOptions: Array<any>;
  newsDaysRange: Array<any>;
  accountsSearchDayRange: Array<any>;
  trademarkClassOptions: Array<any>;
  trademarkStatusOptions: Array<any>;
  companyColumnsNotAllowedToFilter: Array<any>;
  tagsPositiveOptions: Array<any> = [];
  tagsNegativeOptions: Array<any> = [];
  crossSellTagOptions: Array<any> = [];
  alertOptions: Array<any> = [];
  teamMembers: Array<any> = [];
  teamMemberIdToCreateAlertFor: number = null;
  totalEnergyKwhOptions: Array<any>;
  gasKwhOptions: Array<any>;
  electricKwhOptions: Array<any>;
  scope1KwhOptions: Array<any>;
  scope2KwhOptions: Array<any>;
  scope3KwhOptions: Array<any>;
  totalEmissionsKgCo2eOptions: Array<any>;
  scope1KgCo2eOptions: Array<any>;
  scope2KgCo2eOptions: Array<any>;
  scope3KgCo2eOptions: Array<any>;
  kgCo2ePerMillionRevenueOptions: Array<any>;
  kgCo2ePerEmployeeOptions: Array<any>;
  patentFiledWithinDaysOptions: Array<any>;
  patentAwardedWithinDaysOptions: Array<any>;
  contractAwardedWithinMonthsOptions: Array<any>;
  contractStartMonthsOptions: Array<any>;
  contractEndMonthsOptions: Array<any>;
  contractValueOptions: Array<any>;
  chargeCreatedMonthLimits: Array<any>;
  companyNameContains = '';
  accountantNameContains = '';
  bankerNameContains = '';
  auditorNameContains = '';
  lawyerNameContains = '';

  selectedAccountants: Array<any> = [];
  selectedBankers: Array<any> = [];
  selectedAuditors: Array<any> = [];
  selectedLawyers: Array<any> = [];

  accountantSearchInput = '';
  bankerSearchInput = '';
  auditorSearchInput = '';
  lawyerSearchInput = '';

  possibleAccountants: Array<any> = [];
  possibleBankers: Array<any> = [];
  possibleAuditors: Array<any> = [];
  possibleLawyers: Array<any> = [];
  loadingSupplierType: string = '';

  selectedCountries: Array<any> = [];
  selectedCompanyCategories: Array<any> = [];
  selectedSICCodes: Array<any> = [];
  excludedSICCodes: Array<any> = [];
  websiteKeywords: Array<any> = [];
  selectedCompanyStatuses: Array<any> = [];
  corporateStructureIsTopLevelParentCompany = false;
  corporateStructureHasParent = false;
  corporateStructureDoesNotHaveParent = false;
  corporateStructureHasChild = false;
  corporateStructureDoesNotHaveChild = false;
  lowerNumberOfUBOs: number = null;
  upperNumberOfUBOs: number = null;
  selectedUBOCountries: Array<any> = [];
  corporateStructureRelatedCompanies = '';
  hasWebsite = false;
  employeeLowerCount: number = null;
  employeeUpperCount: number = null;
  excludeEstimatedHeadcountFigures = false;
  departmentHeadCountLowerCount: number = null;
  departmentHeadCountUpperCount: number = null;
  lowerHeadCountGrowthPercentage: number = null;
  upperHeadCountGrowthPercentage: number = null;
  selectedFunctionalDepartments: Array<any> = [];
  lowerAge: number = null;
  upperAge: number = null;
  postcodeBeginsWith = '';
  postcodeWithinRadius: string = '';
  postcodeDistrict = '';
  ukRegion: Array<string> = [];
  selectedTownId = '';
  mileRadiusOfPostcode: number = null;
  selectedAddressTypeOption: string = '';
  hasOverseasParent = false;
  hasOfficesOverseas = false;
  selectedOfficeCountries: Array<any> = [];
  selectedGeographicalRevenueCountries: Array<any> = [];
  selectedGeographicalRevenueTerritories: Array<any> = [];
  lowerGeographicalRevenueSelected: number = null;
  upperGeographicalRevenueSelected: number = null;
  lowerNumberOfKnownOfficeLocations: number = null;
  upperNumberOfKnownOfficeLocations: number = null;
  hasLEINumber = false;
  selectedLEIStatus = '';
  selectedImportExportCategories: Array<any> = [];
  selectedImportingOrExportingOption = '';
  importExportTimeScale: number = null;
  importExportFrequencyImportingOrExporting = '';
  importExportFrequency: number = null;
  importExportFrequencyMonthCount: number = null;
  selectedWebTechnologies: Array<any> = [];
  webTrafficLow: number = null;
  webTrafficHigh: number = null;
  lowerAverageDirectorAgeRange: number = null;
  upperAverageDirectorAgeRange: number = null;
  lowerNumberOfDirectors: number = null;
  upperNumberOfDirectors: number = null;
  lowerNumberOfPSCs: number = null;
  upperNumberOfPSCs: number = null;
  recentDirectorshipTitleContains: string = '';
  lowerRecentDirectorshipMonth: number = null;
  upperRecentDirectorshipMonth: number = null;
  PSCsRealPersonsOnly = false;
  nationalitiesOfDirectorsSelected: Array<any> = [];
  nationalitiesOfPSCsSelected: Array<any> = [];
  directorJobTitleContains = '';
  lowerDaysUntilFinancialYearEnd: number = null;
  upperDaysUntilFinancialYearEnd: number = null;
  selectedFinancialYearEnds: Array<any> = [];
  lowerDaysUntilAnniversary: number = null;
  upperDaysUntilAnniversary: number = null;
  isHiring = false;
  hiringJobTitleContains = '';
  hiringDescriptionContains = '';
  selectedHiringDayLimit: number = null;
  lowerNumberOfRolesHiringFor: number = null;
  upperNumberOfRolesHiringFor: number = null;
  selectedNumberOfRolesHiringForDayLimit: number = null;
  includeRecruiters = false;
  amazonSeller = false;
  listedOnGCloud = false;
  hasPhoneNumber = false;
  hasEmailAddress = false;
  hasRaisedInvestment = false;
  hasShareFilingEvent = false;
  shareFilingEventWithinDays: number = null;
  lowerShareFilingCapitalValue: number = null;
  upperShareFilingCapitalValue: number = null;
  lowerInvestmentAmount: number = null;
  upperInvestmentAmount: number = null;
  ignoreRevenueEstimates = false;
  lowerAccountsDueSelected: number = null;
  upperAccountsDueSelected: number = null;
  lowerLatestAccountsSubmittedSelected: number = null;
  upperLatestAccountsSubmittedSelected: number = null;
  selectedLatestAccountsGroupOption = '';
  selectedLatestAccountsAuditOption = '';
  accountsAreOverdue = false;
  accountsSearchTerm = '';
  accountsSearchWithinDays: number = null;
  issuedAGrant = false;
  chargeRegistered = false;
  outstandingChargeRegistered = false;
  noOutstandingCharges = false;
  lowerGrantAmount: number = null;
  upperGrantAmount: number = null;
  grantLowerMonthsUntilProjectEnd: number = null;
  grantUpperMonthsUntilProjectEnd: number = null;
  homepageSearchTerm = '';
  fullWebsiteSearchTerm = '';
  selectedEvents: Array<any> = [];
  mileRadiusOfEventsSelected: number = null;
  postcodeEventsWithinRadius = '';
  eventsWithinDaysLower: number = null;
  eventsWithinDaysUpper: number = null;
  newsWithinDays: number = null;
  newsContaining = '';
  newsSource = '';
  newsEventCategories: Array<string> = [];
  newsEventCategoryGroups = NEWS_EVENT_CATEGORY_GROUPS;
  trademarkClassIdsSelected: Array<any> = [];
  trademarkDescriptionContains = '';
  trademarkNameContains = '';
  selectedTrademarkStatuses: Array<any> = [];
  excludeCompaniesTaggedByTeam = false;
  tagNegativeIdsSelected: Array<any> = [];
  tagPositiveIdsSelected: Array<any> = [];
  tagSearchTerm = '';
  crossSellTagIdsSelected: Array<any> = [];
  crossSellLinkedThroughPeople = false;
  crossSellLinkedThroughCorporateStructure = false;
  selectedCustomColumns: Array<any> = [];
  unreadAlertsOnly = false;
  lowerAlertsDayRange: number = null;
  upperAlertsDayRange: number = null;
  alertIdsSelected: Array<any> = [];
  isLowEnergy = false;
  lowerTotalEnergyKwhSelected: number = null;
  upperTotalEnergyKwhSelected: number = null;
  lowerGasKwhSelected: number = null;
  upperGasKwhSelected: number = null;
  lowerElectricKwhSelected: number = null;
  upperElectricKwhSelected: number = null;
  lowerScope1KwhSelected: number = null;
  upperScope1KwhSelected: number = null;
  lowerScope2KwhSelected: number = null;
  upperScope2KwhSelected: number = null;
  lowerScope3KwhSelected: number = null;
  upperScope3KwhSelected: number = null;
  lowerTotalEmissionsKgCo2eSelected: number = null;
  upperTotalEmissionsKgCo2eSelected: number = null;
  lowerScope1KgCo2eSelected: number = null;
  upperScope1KgCo2eSelected: number = null;
  lowerScope2KgCo2eSelected: number = null;
  upperScope2KgCo2eSelected: number = null;
  lowerScope3KgCo2eSelected: number = null;
  upperScope3KgCo2eSelected: number = null;
  lowerKgCo2ePerMillionRevenueSelected: number = null;
  upperKgCo2ePerMillionRevenueSelected: number = null;
  lowerKgCo2ePerEmployeeSelected: number = null;
  upperKgCo2ePerEmployeeSelected: number = null;

  hasActivePatent = false;
  hasAnyPatentRecord = false;
  patentFiledWithinDays: number = null;
  lowerPatentAwardedDays: number = null;
  upperPatentAwardedDays: number = null;

  lowerContractValueSelected: number = null;
  upperContractValueSelected: number = null;
  contractAwardedWithinMonths: number = null;
  lowerContractStartMonthsSelected: number = null;
  upperContractStartMonthsSelected: number = null;
  lowerContractEndMonthsSelected: number = null;
  upperContractEndMonthsSelected: number = null;
  hasContract = false;
  contractTitleContains = '';
  contractDescriptionContains = '';

  chargeHolderNameContains = '';
  ignoreSatisfiedCharges = false;
  chargeCreatedMonthsAgoLower: number = null;
  chargeCreatedMonthsAgoUpper: number = null;

  supplierTagSelected: number = null;
  associateWithOneSupplierOnly = false;
  customerTagSelected: number = null;

  similarCompanyUrl = '';

  hasLandTitle: boolean = false;
  landTenure: string = '';
  landCategory: string = '';

  sortKey = '';
  sortDirectionIsAscending = false;

  companyCount = signal<
    Partial<{ count: number; exact: boolean; [key: string]: any }>
  >({ count: 5100000, exact: false });

  sicCodeTextSearch = '';
  isSearchingSicCodes = false;
  searchTags: Array<any> = [];
  activeParamsFromMapper: string[] = [];
  importExportTextSearch = '';

  webTechsTextSearch = '';
  isSearchingWebTechs = false;

  checkboxSelectAllCompanies: boolean;
  hasSelectedTotalNumberMatchingFilter = false;
  showSelectedNumbersSection = false;
  totalCompaniesSelected: number = null;

  savedSearchName = '';
  makeSavedSearchShared = false;
  privateSavedSearches: Record<string, any>[] = [];
  sharedSavedSearches: Record<string, any>[] = [];
  loadingSavedSearches = signal(false);

  propensityScoreComponentDescription = '';
  propensityWeightingRange: Array<any> = [];
  propensityTAMLimitCount: number = null;
  propensityScoreWeighting: number = null;
  propensityModels: Array<any> = [];
  editablePropensityModels: Array<any> = [];
  selectedPropensityModelId: number = null;
  propensityScoreTAMName = '';
  stringToTriggerNewPropensityModel = 'STRINGTOTRIGGERNEWPROPENSITYMODEL';
  canEditTAM = false;
  selectedPropensityModelIdToUpdateTAM: number = null;

  savedAlertName = '';
  selectedAlertFrequency = '';
  alertFrequencyOptions: Array<any> = [];
  jsonParamKeysNotAllowedForAlertsOrPropensityScore: Array<any> = [];
  alertsDaysRange: Array<any>;

  territoryName = '';

  hasSemanticSearchAccess: boolean = false;
  summarySemanticSearchTerm: string = '';
  // ZIN-5595: concept-level matching is the only search behaviour; the
  // "Precise matches only" toggle was removed, so this is always true.
  summarySemanticSearchConceptMatch: boolean = true;
  // ZIN-5642: continuous match-quality slider. The value is a minimum
  // displayed match-score floor (0-40) applied backend-side: 0 = Reasonable
  // (loosest, all matches, left), 20 = Good (default, middle), 40 = Excellent
  // (strictest, right). Low-to-high left-to-right like every other slider.
  readonly semanticMatchScoreMax: number = 40;
  readonly defaultSemanticMatchScore: number = 20;
  summarySemanticSearchMinScore: number = 20;
  // ZIN-5643: backend sortKey that maps to SEMANTIC_SEARCH_SCORE_SORT
  // (web_app/_constants.py SEMANTIC_SEARCH_SCORE).
  readonly semanticSearchScoreSortKey: string = 'Products & Services Score';
  clusterSearchMode: boolean = false;
  hasClusterSearchAccess: boolean = false;

  hasAISearchAccess: boolean = false;
  hasAIRAAccess: boolean = false;
  hasPAFAccess: boolean = false;
  hasNYBAccess: boolean = false;
  selectedModules: Record<string, any>[] = []; //has AIRA modules checkboxes

  getRandomPlaceholderCompanyName(placeholderRowIndex: number = 0): string {
    const adjectives = [
      'Blue',
      'Green',
      'Silver',
      'Prime',
      'Swift',
      'Global',
      'Northern',
      'Quantum',
      'Dynamic',
      'Pioneer',
      'Vertex',
      'Summit',
      'Crescent',
      'Harbor',
      'Oakwood',
    ];
    const nouns = [
      'Holdings',
      'Solutions',
      'Industries',
      'Ventures',
      'Group',
      'Partners',
      'Enterprises',
      'Systems',
      'Logistics',
      'Capital',
      'Consulting',
      'Manufacturing',
      'Services',
      'Technologies',
      'Investments',
    ];

    // using random prime numbers for scrambling
    const base = Math.abs(placeholderRowIndex + 7);
    const adj = adjectives[(base * 17 + 11) % adjectives.length];
    const noun = nouns[(base * 31 + 7) % nouns.length];
    return `${adj} ${noun} LTD`;
  }

  getPlaceholderRowCount(): number {
    const locked = this.companyCount()?.locked_mi_companies || 0;
    const pageSize = this.selectedPageSizeLimit || 0;
    const current = this.companies ? this.companies.length : 0;
    if (locked <= 0 || pageSize <= 0) return 0;
    const remaining = pageSize - current;
    if (remaining <= 0) return 0;
    // Fill up to page size unless total would be less than a full page
    return Math.min(locked, remaining);
  }

  getPlaceholderRowIndices(): number[] {
    const n = this.getPlaceholderRowCount();
    return Array.from({ length: n }, (_, i) => i);
  }

  unlockMagicInsightsFromResults(): void {
    this.openMagicInsightsUnlockModal();
  }

  unlockPlaceholderMagicInsights(): void {
    const lockedOnPage = this.getPlaceholderRowCount();
    this.openMagicInsightsUnlockModal(lockedOnPage || null);
  }

  refreshAiraConfigs(): void {
    this.dashboardService.getAiraFilterConfig().subscribe(configData => {
        this.updateAiraFilterDetails(configData);
    });
  }

  private openMagicInsightsUnlockModal(presetCount?: number | null): void {
    // Force-selecting every row on the current page used to stand in for "these
    // results". It actually scoped the request to that page rather than the full
    // filtered search, and left the selection bar showing after the modal closed
    // with nothing submitted. Leaving the existing selection alone lets the modal
    // fall back to the full search scope (via numberOfCompaniesMatchingSearchParams
    // and this preset count) unless the user had already ticked companies themselves.
    this.dataStore.setMagicInsightsUnlockCount(presetCount ?? null);
    this.openGetInsights();
  }

  openGetInsights(): void {
    this.cdr.detectChanges();
    this.getInsights.open();
  }

  private resetMagicInsightsUnlockPreset(): void {
    this.dataStore.setMagicInsightsUnlockCount(null);
  }

  handleGetInsightsCompleted(event: any): void {
    if (!event?.isOrganisationModule) {
      this.searchCompaniesAfterFilters(false);
    }
  }

  columnsToHide: Array<any> = [
    'id',
    'number',
    'isPlaceholderCompany',
    'hasViewed',
    'hasMIAvailable',
  ];

  pageNumber = 1;
  maxPageNumber: number = null;

  isSuperUser: boolean;
  emailOnAccount: any;
  isReadOnlyUser: any;
  hasPropensityScoreAccess: any;
  hasDeepCrawlAccess: any;
  hasEnergyAndEmissionsAccess: any;
  hasPatentsAccess: any;
  hasShareFilingsAccess: any;
  hasGovernmentContractsAccess: any;
  hasCompanyChargesAccess: any;
  hasCrossSellAccess: any;
  hasRecentAccountsAccess: any;
  hasSupplierDataAccessMessage: any;
  hasTerritoryAssignmentAccessMessage: any;
  hasHubspotAccess: any;
  maxPageSizeLimit: number;
  selectedPageSizeLimit: number;

  isSidebarCollapsed = signal(false);
  mobileSidebarRolledUp = false;
  mobileSidebarBtnFixedTop = false;
  expandedFilterSections: Record<string, boolean> = {};
  confidenceOpen: Record<string, boolean> = {};
  hoveredOverflowingTables: Record<string, any> = {};

  townName: string = '';
  possibleTownNames: Array<any>;

  websiteKeywordInput: string = '';
  possibleWebsiteKeywords: Array<any>;

  eventNameSearchInput: string = '';
  possibleMatchingEvents: Array<any>;

  customColumns: Array<any>;
  customColumnSearchTerm = '';
  filteredCustomColumns: Array<any> = [];
  customColumnNamesToObjects: any = {};

  hoveredColIndex: number = null;
  currentHoverColumnName = '';
  currentHoverRowName = '';
  currentlyEditingColumnName = '';
  currentlyEditingRowName = '';

  tableHeaderColumnNumberCurrentlyClicked: number = null;
  tableColumnNumberCurrentlyHovered: number = null;
  wrappedTableColumnNumbers: Array<number> =
    JSON.parse(localStorage.getItem('zintWrappedColumns')) || [];
  teamMembersForCustomColumns: Array<any> = [];
  columnNamesToWidths: any = {};
  clickedRow: number = null;
  clickedColumn: number = null;

  geographyPostcodeBeginsWithIsEditing = false;
  geographyPostcodeDistrictIsEditing = false;
  geographyWithinRadiusOfPostcodeIsEditing = false;
  geographyWithinRadiusOfTownIsEditing = false;

  selectAllVisibleFunctionalDepartments = false;
  functionalDepartmentTextSearch = '';
  isSearchingFunctionalDepartments = false;
  hasSearchedFunctionalDepartment = false;

  geographicalRevenueTextSearch = '';

  individualViews: Array<any> = [];
  organisationViews: Array<any> = [];
  selectedView: Record<string, any> = null;
  loadResultsView = false;

  availableTerritories: Array<any> = [];
  areTerritoriesEnforced: false;
  selectedTerritory: Record<string, any> = null;

  sliderValues: Record<string, any> = {};
  configTextInputValues: Record<string, any> = {};
  configMultiSelectValues: Record<string, any> = {};
  confidenceValues: Record<string, { lower: number | null; upper: number | null }> = {};

  savedSearchParamName: string = '';

  resultsActionModalTitle: string = '';

  availableCRMList: Record<string, any>[] = [];
  crmExporterName: string = '';
  isCRMExportDisabled = false;

  filterSearchTerm: string = '';
  filterSearchErrorMsg: string = '';

  dynamicFilterIdToKeywordsMappings: Record<string, string[]> = {}; // <-- ADD THIS

  profilePreviewIsOpen = signal(false);

  @ViewChild('profilePreview') previewDrawer: MatDrawer;
  @ViewChild('getInsights') getInsights: GetInsightsComponent;
  @ViewChild('numberUBOsSliderElement')
  numberUBOsSliderElement: ZintNgxSliderComponent;
  @ViewChild('companyAgeSliderElement')
  companyAgeSliderElement: ZintNgxSliderComponent;
  @ViewChild('mileRadiusSliderElement')
  mileRadiusSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfKnownOfficeLocationsSliderElement')
  numberOfKnownOfficeLocationsSliderElement: ZintNgxSliderComponent;
  @ViewChild('geographicalRevenueSliderElement')
  geographicalRevenueSliderElement: ZintNgxSliderComponent;
  @ViewChild('accountsDueSliderElement')
  accountsDueSliderElement: ZintNgxSliderComponent;
  @ViewChild('latestAccountsSubmittedSliderElement')
  latestAccountsSubmittedSliderElement: ZintNgxSliderComponent;
  @ViewChild('investmentRaisedSliderElement')
  investmentRaisedSliderElement: ZintNgxSliderComponent;
  @ViewChild('shareFilingEventWithinDaysSliderElement')
  shareFilingEventWithinDaysSliderElement: ZintNgxSliderComponent;
  @ViewChild('shareFilingCapitalValueSliderElement')
  shareFilingCapitalValueSliderElement: ZintNgxSliderComponent;
  @ViewChild('grantAmountSliderElement')
  grantAmountSliderElement: ZintNgxSliderComponent;
  @ViewChild('grantMonthsUntilProjectEndSliderElement')
  grantMonthsUntilProjectEndSliderElement: ZintNgxSliderComponent;
  @ViewChild('headcountSliderElement')
  headcountSliderElement: ZintNgxSliderComponent;
  @ViewChild('headcountGrowthSliderElement')
  headcountGrowthSliderElement: ZintNgxSliderComponent;
  @ViewChild('departmentHeadcountSliderElement')
  departmentHeadcountSliderElement: ZintNgxSliderComponent;
  @ViewChild('averageDirectorAgeSliderElement')
  averageDirectorAgeSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfDirectorsSliderElement')
  numberOfDirectorsSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfPSCsSliderElement')
  numberOfPSCsSliderElement: ZintNgxSliderComponent;
  @ViewChild('recentDirectorshipMonthRangeSliderElement')
  recentDirectorshipMonthRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('importExportSliderElement')
  importExportSliderElement: ZintNgxSliderComponent;
  @ViewChild('importExportFrequencySliderElement')
  importExportFrequencySliderElement: ZintNgxSliderComponent;
  @ViewChild('importExportFrequencyMonthCountSliderElement')
  importExportFrequencyMonthCountSliderElement: ZintNgxSliderComponent;
  @ViewChild('websiteVisitorsSliderElement')
  websiteVisitorsSliderElement: ZintNgxSliderComponent;
  @ViewChild('hiringDayLimitSliderElement')
  hiringDayLimitSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfRolesHiringForSliderElement')
  numberOfRolesHiringForSliderElement: ZintNgxSliderComponent;
  @ViewChild('numberOfRolesHiringForDayLimitSliderElement')
  numberOfRolesHiringForDayLimitSliderElement: ZintNgxSliderComponent;
  @ViewChild('daysUntilFinancialYearEndSliderElement')
  daysUntilFinancialYearEndSliderElement: ZintNgxSliderComponent;
  @ViewChild('daysUntilAnniversarySliderElement')
  daysUntilAnniversarySliderElement: ZintNgxSliderComponent;
  @ViewChild('eventsMileRadiusSliderElement')
  eventsMileRadiusSliderElement: ZintNgxSliderComponent;
  @ViewChild('eventsDayRangeSliderElement')
  eventsDayRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('newsDayRangeSliderElement')
  newsDayRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('accountsSearchDayRangeSliderElement')
  accountsSearchDayRangeSliderElement: ZintNgxSliderComponent;
  @ViewChild('alertsDayRangeSliderElement')
  alertsDayRangeSliderElement: ZintNgxSliderComponent;

  @ViewChild('totalEnergyKwhSliderElement')
  totalEnergyKwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('gasKwhSliderElement')
  gasKwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('electricKwhSliderElement')
  electricKwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope1KwhSliderElement')
  scope1KwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope2KwhSliderElement')
  scope2KwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope3KwhSliderElement')
  scope3KwhSliderElement: ZintNgxSliderComponent;
  @ViewChild('totalEmissionsKgCo2eSliderElement')
  totalEmissionsKgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope1KgCo2eSliderElement')
  scope1KgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope2KgCo2eSliderElement')
  scope2KgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('scope3KgCo2eSliderElement')
  scope3KgCo2eSliderElement: ZintNgxSliderComponent;
  @ViewChild('kgCo2ePerMillionRevenueSliderElement')
  kgCo2ePerMillionRevenueSliderElement: ZintNgxSliderComponent;
  @ViewChild('kgCo2ePerEmployeeSliderElement')
  kgCo2ePerEmployeeSliderElement: ZintNgxSliderComponent;
  @ViewChild('patentFiledWithinDaysSliderElement')
  patentFiledWithinDaysSliderElement: ZintNgxSliderComponent;
  @ViewChild('patentAwardedWithinDaysSliderElement')
  patentAwardedWithinDaysSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractValueSliderElement')
  contractValueSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractAwardedWithinMonthsSliderElement')
  contractAwardedWithinMonthsSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractStartMonthsSliderElement')
  contractStartMonthsSliderElement: ZintNgxSliderComponent;
  @ViewChild('contractEndMonthsSliderElement')
  contractEndMonthsSliderElement: ZintNgxSliderComponent;
  @ViewChild('chargeCreatedSliderElement')
  chargeCreatedSliderElement: ZintNgxSliderComponent;

  @ViewChildren('heatScoreSliders')
  heatScoreSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('fleetSliders')
  fleetSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('wagesSliders')
  wagesSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('numberOfActiveTrademarksSliders')
  numberOfActiveTrademarksSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('tanAssetsSliders')
  tanAssetsSliders: QueryList<ZintNgxSliderComponent>;
  @ViewChildren('intanAssetsSliders')
  intanAssetsSliders: QueryList<ZintNgxSliderComponent>;

  @ViewChildren('genericConfigSliders')
  genericConfigSliders: QueryList<ZintNgxSliderComponent>;

  @ViewChildren('confidenceSliders')
  confidenceSliders: QueryList<ZintNgxSliderComponent>;

  allConfigSlidersChildren: ZintNgxSliderComponent[] = [];

  // Buffer for slider updates when components are not yet rendered
  pendingSliderUpdates: Record<
    string,
    { value: number; airaQuestionId?: number | string | null }
  > = {};

  bufferSliderUpdate(
    key: string,
    value: number,
    airaQuestionId: number | string | null = null
  ): void {
    this.pendingSliderUpdates[key] = { value, airaQuestionId };
  }

  rebuildAllConfigSlidersChildren(): void {
    this.allConfigSlidersChildren = [
      ...this.tanAssetsSliders,
      ...this.intanAssetsSliders,
      ...this.heatScoreSliders,
      ...this.fleetSliders,
      ...this.wagesSliders,
      ...this.numberOfActiveTrademarksSliders,
      ...this.genericConfigSliders,
    ];
  }

  applyPendingSliderUpdates(): void {
    this.rebuildAllConfigSlidersChildren()
    const entries = Object.entries(this.pendingSliderUpdates);
    if (!entries.length) return;
    entries.forEach(([key, entry]) => {
      delete this.pendingSliderUpdates[key];
      this.seeIfNeedToUpdateSlider(key, entry.value, entry.airaQuestionId);
    });
    this.refreshSearchTagsFromCurrentFilters()
  }

  refreshSearchTagsFromCurrentFilters(): void {
    try {
      this.updateFilters();
      const params = this.allFilterParams();
      // populate search tags
      this.searchTags = JSON.parse(JSON.stringify(params)).updates;
      this.getActiveSearchParams();
    } catch (_) {
    }
  }


  isLoading = this.dashboardService.isLoading;
  isLoadingZintPropensityScore = this.zintPropensityScoreService.isLoading;

  MIAvailabilityStatus = {
    NoMI: 0,
    hasMI: 1
  } as const;

  hasLoadedFlags = {
    hasLoadedUIData: signal(false),
    hasLoadedUserTags: signal(false),
    hasLoadedUserCrossSellTags: signal(false),
    hasLoadedCustomColumns: signal(false),
    hasLoadedAlertOptions: signal(false),
    hasLoadedBackendSliders: signal(false),
    hasLoadedAiraFilters: signal(false),
  };

  allEndpointsLoaded = computed(() => {
    const allFlags = Object.values(this.hasLoadedFlags).map(signalFn =>
      signalFn()
    );
    return allFlags.every(val => val);
  });

  freezeHeadersIsActive = false;
  isWrapAllColumns =
    (JSON.parse(localStorage.getItem('zintAllColumnsWrapped')) as boolean) ||
    false;
  companyResultsTableHeaderRowMarginLeft = 0;
  isCompanyResultsTableHeaderRowFixed = false;
  resultTableScrollLeft: number = 0;
  showFakeCompanyNameAndCheckboxSection = false;
  horizontalScrollCutoffToShowFakeHeaders = 14;
  lastTableHorizontalPosition = 0;
  startingHeaderRowOffsetTop: number = null;

  zintSlidersConfigs: Record<string, any> = {};
  zintTextInputConfigs: Record<string, any> = {};
  zintMultiSelectConfigs: Record<string, any> = {};
  sliderFieldLabels: string[] = [];

  keyToSliderElementMap: Record<string, any> = {};

  tangibleCategories = [
    'Land And Buildings',
    'Plant And Machinery',
    'Furniture And Fittings',
    'Vehicles',
    'Computer Equipment',
    'Leasehold Assets',
    'Assets Under Construction',
    'Other',
  ];

  inTangibleCategories = [
    'Goodwill',
    'Computer Software',
    'Patents, Trademarks, and Licences',
    'Other Intangible Assets',
  ];

  landOwnershipAndDevelopmentSliders = [];
  sliderSections = []; // this should be renamed to financial sections
  rawAiraFiltersData = [];
  linkedAiraFilters: Record<string, any> = {};
  // Metadata keys to exclude from filter rendering in AIRA modules
  airaMetadataKeys = new Set(['id', 'hasFilterAccess', 'isEditable', 'newQuestion', 'answerCount', 'companiesCount', 'lastAnswerGeneratedAt']);
  magicCountriesList = extendedCountriesList;
  magicModuleNamesIds: Record<string, any>[] = [];
  nonEditableMagicModuleNamesIds: Record<string, any>[] = [];
  editableMagicModuleNamesIds: Record<string, any>[] = [];
  organisationMagicModuleNamesIds: Record<string, any>[] = [];

  isScreenTooSmall = computed(() =>
    this.pubMethods.isScreenSmallerThanBreakpoint(650)()
  );
  previewDrawerWidth: Signal<string> = signal('820px');

  ngOnInit() {
    this.urlQueryParams = this.route.snapshot.queryParams;

    this.zintSlidersConfigs = {
      tangibleAssets,
      intangibleAssets,
      heatScore,
      fleet,
      wages,
      numberOfActiveTrademarks,
      esosNotifications,
      epcEnergy,
    };
    this.sliderFieldLabels = Object.values(this.zintSlidersConfigs)
      .flat()
      .map(asset => asset.label);

    this.dynamicFilterIdToKeywordsMappings = { ...filterIdToKeywordsMappings };

    this.bindScrollBars();
    this.updateScrollBarDimensions();
    this.getSliderDetails();
    this.getUIDataAndUpdateCompanies(); // this is the master function. updates all UI components and updates companies.
    this.taggingService
      .getUserTags(false)
      .subscribe(output => this.updateTagOptions(output));
    this.taggingService
      .getUserCrossSellTags(false)
      .subscribe(output => this.updateCrossSellTagOptions(output));
    this.customColumnService
      .getCustomColumns()
      .subscribe(output => this.updateCustomColumnsAndColumnWidths(output));
    this.alertsService
      .getBasicAlertDetails()
      .subscribe(output => this.updateAlerts(output));
    this.zintPropensityScoreService
      .getUserPropensityScoreDetails(false)
      .subscribe(output => this.updateZintPropensityScoreDetails(output));
    this.resultsFormatEditorService
      .getBasicUserResultsFormat()
      .subscribe(data => this.updateAvailableUserViews(data));
    this.companyCount().count = 5100000;
    this.companyCount().exact = false;
    this.getCompanyCount(true);
    this.communicationService.getIsSuperUserMessage().subscribe(isSuperUser => {
      this.isSuperUser = isSuperUser;
    });
    this.communicationService.getEmailMessage().subscribe(accountEmail => {
      this.emailOnAccount = accountEmail;
    });
    this.communicationService
      .getIsReadOnlyUserMessage()
      .subscribe(isReadOnlyUser => {
        this.isReadOnlyUser = isReadOnlyUser;
      });
    this.communicationService
      .getHasPropensityScoreAccessMessage()
      .subscribe(hasPropensityScoreAccess => {
        this.hasPropensityScoreAccess = hasPropensityScoreAccess;
      });
    this.communicationService
      .getHasDeepCrawlAccessMessage()
      .subscribe(hasDeepCrawlAccess => {
        this.hasDeepCrawlAccess = hasDeepCrawlAccess;
      });
    this.communicationService
      .getHasEnergyAndEmissionsAccessMessage()
      .subscribe(hasEnergyAndEmissionsAccess => {
        this.hasEnergyAndEmissionsAccess = hasEnergyAndEmissionsAccess;
      });
    this.communicationService
      .getHasPatentsAccessMessage()
      .subscribe(hasPatentsAccess => {
        this.hasPatentsAccess = hasPatentsAccess;
      });
    this.communicationService
      .getHasShareFilingsAccessMessage()
      .subscribe(hasShareFilingsAccess => {
        this.hasShareFilingsAccess = hasShareFilingsAccess;
      });
    this.communicationService
      .getHasGovernmentContractsAccessMessage()
      .subscribe(hasGovernmentContractsAccess => {
        this.hasGovernmentContractsAccess = hasGovernmentContractsAccess;
      });
    this.communicationService
      .getHasCompanyChargesAccessAccessMessage()
      .subscribe(hasCompanyChargesAccess => {
        this.hasCompanyChargesAccess = hasCompanyChargesAccess;
      });
    this.communicationService
      .getHasCrossSellAccessMessage()
      .subscribe(hasCrossSellAccess => {
        this.hasCrossSellAccess = hasCrossSellAccess;
      });
    this.communicationService
      .getHasRecentAccountsAccessMessage()
      .subscribe(hasRecentAccountsAccess => {
        this.hasRecentAccountsAccess = hasRecentAccountsAccess;
      });
    this.communicationService
      .getHasSupplierDataAccessMessage()
      .subscribe(hasSupplierDataAccessMessage => {
        this.hasSupplierDataAccessMessage = hasSupplierDataAccessMessage;
      });
    this.communicationService.getHubspotAccess().subscribe(hasHubspotAccess => {
      this.hasHubspotAccess = hasHubspotAccess;
    });
    this.communicationService
      .getHasTerritoryAssignmentAccessMessage()
      .subscribe(hasTerritoryAssignmentAccessMessage => {
        this.updateTerritoryAssignmentDetails(
          hasTerritoryAssignmentAccessMessage
        );
      });
    this.communicationService
      .getHasSemanticSearchAccessMessage()
      .subscribe(hasSemanticSearchAccessMessage => {
        this.hasSemanticSearchAccess = hasSemanticSearchAccessMessage;
      });
    this.communicationService
      .getHasClusterSearchAccess()
      .subscribe(hasClusterSearchAccess => {
        this.hasClusterSearchAccess = hasClusterSearchAccess;
      });
    this.communicationService
      .getAISearchAccess()
      .subscribe(hasAISearchAccess => {
        this.hasAISearchAccess = hasAISearchAccess;
      });
    this.communicationService.getHasAIRAAccess().subscribe(hasAIRAAccess => {
      this.hasAIRAAccess = hasAIRAAccess;
    });
    this.communicationService.getHasPAFAccess().subscribe(hasPAFAccess => {
      this.hasPAFAccess = hasPAFAccess;
    });
    this.communicationService.getHasNYBAccess().subscribe(hasNYBAccess => {
      this.hasNYBAccess = hasNYBAccess;
    });
    this.communicationService
      .getPageLimitFromPlanMessage()
      .subscribe(pageLimit => {
        this.maxPageSizeLimit = pageLimit;
        this.selectedPageSizeLimit =
          this.maxPageSizeLimit > 10 ? 50 : this.maxPageSizeLimit;
      });

    this.crmService.getAvailableCRMs().subscribe(data => {
      this.availableCRMList = data;
    });

    this.pubMethods.showIEWarningIfRequired();

  }

  updateAvailableUserViews(data) {
    this.individualViews = data.individual;
    this.organisationViews = data.organisation;
  }

  layoutForMobile(): void {
    if (this.isScreenTooSmall()) {
      this.disableFixedHeaders();
    }
  }

  toggleFixedHeaders() {
    if (this.freezeHeadersIsActive) {
      this.disableFixedHeaders();
    } else {
      this.enableFixedHeaders();
    }
  }

  enableFixedHeaders() {
    this.freezeHeadersIsActive = true;
    const self = this;

    $('#table-results-section').on('scroll', function () {
      //to show a thin border line when side scrolling
      self.resultTableScrollLeft = $('#table-results-section').scrollLeft();
    });
  }

  disableFixedHeaders() {
    this.freezeHeadersIsActive = false;
    $('#table-results-section').off('scroll');
  }

  bindScrollBars() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(debounceTime(130))
        .subscribe(() => {
          this.ngZone.run(() => this.updateScrollBarDimensions());
        });
    });

    $('.dummy-top-scrollbar-wrapper').mouseup(function () {
      $('#table-results-section').scrollLeft(
        $('.dummy-top-scrollbar-wrapper').scrollLeft()
      );
    });
  }

  mapKeyToSliderElement(): void {
    // this map is to keep the seeIfNeedToUpdateSlider method DRY
    this.keyToSliderElementMap = {
      lowerNumberOfUBOs: this.numberUBOsSliderElement,
      upperNumberOfUBOs: this.numberUBOsSliderElement,
      lowerAge: this.companyAgeSliderElement,
      upperAge: this.companyAgeSliderElement,
      employeeLowerCount: this.headcountSliderElement,
      employeeUpperCount: this.headcountSliderElement,
      lowerHeadCountGrowthPercentage: this.headcountGrowthSliderElement,
      upperHeadCountGrowthPercentage: this.headcountGrowthSliderElement,
      departmentHeadCountLowerCount: this.departmentHeadcountSliderElement,
      departmentHeadCountUpperCount: this.departmentHeadcountSliderElement,
      mileRadiusOfPostcode: this.mileRadiusSliderElement,
      lowerNumberOfKnownOfficeLocations:
        this.numberOfKnownOfficeLocationsSliderElement,
      upperNumberOfKnownOfficeLocations:
        this.numberOfKnownOfficeLocationsSliderElement,
      lowerGeographicalRevenueSelected: this.geographicalRevenueSliderElement,
      upperGeographicalRevenueSelected: this.geographicalRevenueSliderElement,
      importExportTimeScale: this.importExportSliderElement,
      importExportFrequency: this.importExportFrequencySliderElement,
      importExportFrequencyMonthCount:
        this.importExportFrequencyMonthCountSliderElement,
      webTrafficLow: this.websiteVisitorsSliderElement,
      webTrafficHigh: this.websiteVisitorsSliderElement,
      lowerAverageDirectorAgeRange: this.averageDirectorAgeSliderElement,
      upperAverageDirectorAgeRange: this.averageDirectorAgeSliderElement,
      lowerNumberOfDirectors: this.numberOfDirectorsSliderElement,
      upperNumberOfDirectors: this.numberOfDirectorsSliderElement,
      lowerNumberOfPSCs: this.numberOfPSCsSliderElement,
      upperNumberOfPSCs: this.numberOfPSCsSliderElement,
      lowerRecentDirectorshipMonth:
        this.recentDirectorshipMonthRangeSliderElement,
      upperRecentDirectorshipMonth:
        this.recentDirectorshipMonthRangeSliderElement,
      lowerDaysUntilFinancialYearEnd:
        this.daysUntilFinancialYearEndSliderElement,
      upperDaysUntilFinancialYearEnd:
        this.daysUntilFinancialYearEndSliderElement,
      lowerDaysUntilAnniversary: this.daysUntilAnniversarySliderElement,
      upperDaysUntilAnniversary: this.daysUntilAnniversarySliderElement,
      selectedHiringDayLimit: this.hiringDayLimitSliderElement,
      lowerNumberOfRolesHiringFor: this.numberOfRolesHiringForSliderElement,
      upperNumberOfRolesHiringFor: this.numberOfRolesHiringForSliderElement,
      selectedNumberOfRolesHiringForDayLimit:
        this.numberOfRolesHiringForDayLimitSliderElement,
      lowerInvestmentAmount: this.investmentRaisedSliderElement,
      upperInvestmentAmount: this.investmentRaisedSliderElement,
      shareFilingEventWithinDays: this.shareFilingEventWithinDaysSliderElement,
      lowerShareFilingCapitalValue: this.shareFilingCapitalValueSliderElement,
      upperShareFilingCapitalValue: this.shareFilingCapitalValueSliderElement,
      lowerAccountsDueSelected: this.accountsDueSliderElement,
      upperAccountsDueSelected: this.accountsDueSliderElement,
      lowerLatestAccountsSubmittedSelected:
        this.latestAccountsSubmittedSliderElement,
      upperLatestAccountsSubmittedSelected:
        this.latestAccountsSubmittedSliderElement,
      lowerGrantAmount: this.grantAmountSliderElement,
      upperGrantAmount: this.grantAmountSliderElement,
      grantLowerMonthsUntilProjectEnd:
        this.grantMonthsUntilProjectEndSliderElement,
      grantUpperMonthsUntilProjectEnd:
        this.grantMonthsUntilProjectEndSliderElement,
      mileRadiusOfEventsSelected: this.eventsMileRadiusSliderElement,
      eventsWithinDaysLower: this.eventsDayRangeSliderElement,
      eventsWithinDaysUpper: this.eventsDayRangeSliderElement,
      newsWithinDays: this.newsDayRangeSliderElement,
      accountsSearchWithinDays: this.accountsSearchDayRangeSliderElement,
      lowerAlertsDayRange: this.alertsDayRangeSliderElement,
      upperAlertsDayRange: this.alertsDayRangeSliderElement,
      lowerTotalEnergyKwhSelected: this.totalEnergyKwhSliderElement,
      upperTotalEnergyKwhSelected: this.totalEnergyKwhSliderElement,
      lowerGasKwhSelected: this.gasKwhSliderElement,
      upperGasKwhSelected: this.gasKwhSliderElement,
      lowerElectricKwhSelected: this.electricKwhSliderElement,
      upperElectricKwhSelected: this.electricKwhSliderElement,
      lowerScope1KwhSelected: this.scope1KwhSliderElement,
      upperScope1KwhSelected: this.scope1KwhSliderElement,
      lowerScope2KwhSelected: this.scope2KwhSliderElement,
      upperScope2KwhSelected: this.scope2KwhSliderElement,
      lowerScope3KwhSelected: this.scope3KwhSliderElement,
      upperScope3KwhSelected: this.scope3KwhSliderElement,
      lowerTotalEmissionsKgCo2eSelected: this.totalEmissionsKgCo2eSliderElement,
      upperTotalEmissionsKgCo2eSelected: this.totalEmissionsKgCo2eSliderElement,
      lowerScope1KgCo2eSelected: this.scope1KgCo2eSliderElement,
      upperScope1KgCo2eSelected: this.scope1KgCo2eSliderElement,
      lowerScope2KgCo2eSelected: this.scope2KgCo2eSliderElement,
      upperScope2KgCo2eSelected: this.scope2KgCo2eSliderElement,
      lowerScope3KgCo2eSelected: this.scope3KgCo2eSliderElement,
      upperScope3KgCo2eSelected: this.scope3KgCo2eSliderElement,
      lowerKgCo2ePerMillionRevenueSelected:
        this.kgCo2ePerMillionRevenueSliderElement,
      upperKgCo2ePerMillionRevenueSelected:
        this.kgCo2ePerMillionRevenueSliderElement,
      lowerKgCo2ePerEmployeeSelected: this.kgCo2ePerEmployeeSliderElement,
      upperKgCo2ePerEmployeeSelected: this.kgCo2ePerEmployeeSliderElement,
      patentFiledWithinDays: this.patentFiledWithinDaysSliderElement,
      lowerPatentAwardedDays: this.patentAwardedWithinDaysSliderElement,
      upperPatentAwardedDays: this.patentAwardedWithinDaysSliderElement,
      lowerContractValueSelected: this.contractValueSliderElement,
      upperContractValueSelected: this.contractValueSliderElement,
      contractAwardedWithinMonths:
        this.contractAwardedWithinMonthsSliderElement,
      lowerContractStartMonthsSelected: this.contractStartMonthsSliderElement,
      upperContractStartMonthsSelected: this.contractStartMonthsSliderElement,
      lowerContractEndMonthsSelected: this.contractEndMonthsSliderElement,
      upperContractEndMonthsSelected: this.contractEndMonthsSliderElement,
      chargeCreatedMonthsAgoLower: this.chargeCreatedSliderElement,
      chargeCreatedMonthsAgoUpper: this.chargeCreatedSliderElement,
      // ... add any other mappings that are required
    };
  }

  isUpperSlider(key: string): boolean {
    /* check upper as the single sliders now use upperValue */
    const lowerCaseKey = key.toLowerCase();
    if (lowerCaseKey.includes('webtraffic')) {
      // for webTrafficLow and High case
      return !lowerCaseKey.includes('low');
    } else {
      // all other cases
      return !lowerCaseKey.includes('lower');
    }
  }

  updateZintSliderElement(
    sliderToUpdate: IZintSliderElementUpdate,
    airaQuestionId: number | string | null = null
  ): void {
    const { value, key } = sliderToUpdate;
    const isUpper = this.isUpperSlider(key);
    const sliderElement = this.keyToSliderElementMap[key];
    if (!sliderElement) {
      // component not ready yet; buffer for later
      this.bufferSliderUpdate(key, value, airaQuestionId);
      return;
    }
    if (isUpper) {
      sliderElement.updateSliderValues({
        upperUpdate: value,
      });
    } else {
      sliderElement.updateSliderValues({
        lowerUpdate: value,
      });
    }
  }

  updateReusableSlider(
    key: string,
    value: string | number,
    airaQuestionId: number | string | null = null
  ) {
    const propsKey = key.slice(0, 5); // upper or lower
    const updateKey = propsKey === 'lower' ? 'lowerUpdate' : 'upperUpdate';
    const sliderLabel = key.slice(5);

    // bail out if not one of the known config sliders
    if (!this.sliderFieldLabels.includes(sliderLabel)) return;

    // Magic Insights filter labels can collide across modules (e.g. the same
    // "Estimated Currency Volume" slider exists in several FX modules). When an
    // airaQuestionId is supplied (e.g. when reconstructing a saved search) we
    // must match the slider for that specific question; otherwise we can pick a
    // slider belonging to a different module and send the wrong airaQuestionId,
    // which makes the backend return zero results.
    const sliderToUpdate = this.allConfigSlidersChildren?.find(component => {
      const config = component.sliderConfig;
      if (config?.label !== sliderLabel) return false;
      if (airaQuestionId != null && config?.airaQuestionId != null) {
        return config.airaQuestionId === airaQuestionId;
      }
      return true;
    });

    if (sliderToUpdate) {
      sliderToUpdate.updateSliderValues({
        [updateKey]: Number(value),
      });
    } else {
      // Not yet available; buffer for later
      this.bufferSliderUpdate(key, Number(value), airaQuestionId);
    }
  }

  seeIfNeedToUpdateSlider(
    key: string,
    value: any,
    airaQuestionId: number | string | null = null
  ) {
    let intValue = parseInt(value);

    if (isNaN(intValue) && this.pubMethods.isValidJson(value)) {
      // for aira slider values which are object
      const parsed = JSON.parse(value);
      intValue = parseInt(parsed.value);
      // Carry the question id from the saved filter so the correct module's
      // slider is matched even when the filter label is shared across modules.
      if (parsed && parsed.airaQuestionId != null) {
        airaQuestionId = parsed.airaQuestionId;
      }

      if (
        parsed &&
        (parsed.hasOwnProperty('lowerConfidence') ||
          parsed.hasOwnProperty('upperConfidence'))
      ) {
        this.updateConfidenceSliderUIFromSavedParam(key, parsed);
      }

    }

    this.updateZintSliderElement({ key, value: intValue }, airaQuestionId);
    if (key !== 'pageNumber') {
      this.updateReusableSlider(key, intValue, airaQuestionId);
    }
  }


  updateConfidenceSliderUIFromSavedParam(
    key: string,
    parsed: Record<string, any>
  ): void {
    const labelFromKey =
      key.startsWith('lower') || key.startsWith('upper') ? key.slice(5) : key;
    const airaId = parsed?.airaQuestionId;
    if (!airaId || !labelFromKey) return;

    const lowerConf =
      parsed.lowerConfidence === undefined || parsed.lowerConfidence === null
        ? null
        : Number(parsed.lowerConfidence);
    const upperConf =
      parsed.upperConfidence === undefined || parsed.upperConfidence === null
        ? null
        : Number(parsed.upperConfidence);

    // Set in confidenceValues
    const confKey = `confidence:${labelFromKey}Aira${airaId}`;
    this.confidenceValues[confKey] = { lower: lowerConf, upper: upperConf };
    // Then update UI
    this.updateConfidenceSliderUI(airaId, labelFromKey);
  }

  updateNonSliderConfigFilters(key: string, value: any) {
    if (key === 'pageSize') return;
    if (this.pubMethods.isValidJson(value)) {
      const parsed = JSON.parse(value);
      if (parsed.filterType === 'text_input') {
        this.updateConfigTextFilters(key, parsed);
      } else if (parsed.filterType === 'multi_select') {
        this.updateConfigMultiSelectFilters(key, parsed);
      }
      // Add more filterType as needed
    }
  }

  updateConfigMultiSelectFilters(key: string, updatedValue: any) {
    Object.values(this.zintMultiSelectConfigs).forEach((array: any[]) => {
      const foundObj = array.find(obj => {
        return obj.label === key;
      });

      if (foundObj && foundObj.options) {
        foundObj.options.forEach(option => {
          if (updatedValue.value.includes(option.value)) {
            option.checked = true;
          }
        });

        // If confidence arrived via saved search, set and reflect in UI
        if (updatedValue.hasOwnProperty('lowerConfidence') || updatedValue.hasOwnProperty('upperConfidence')) {
          const lower = updatedValue.lowerConfidence === undefined || updatedValue.lowerConfidence === null ? null : Number(updatedValue.lowerConfidence);
          const upper = updatedValue.upperConfidence === undefined || updatedValue.upperConfidence === null ? null : Number(updatedValue.upperConfidence);
          const confKey = `confidence:${foundObj.label}Aira${foundObj.airaQuestionId}`;
          this.confidenceValues[confKey] = { lower, upper };
          this.updateConfidenceSliderUI(foundObj.airaQuestionId, foundObj.label);
        }
        this.triggerConfigMultiSelectInputWithObjectUpdate(foundObj);
      }
    });
  }

  updateConfigTextFilters(key: string, updatedValue: any) {
    Object.values(this.zintTextInputConfigs).forEach((array: any[]) => {
      const foundObj = array.find(obj => {
        return obj.label === key;
      });

      if (foundObj) {
        foundObj.value = updatedValue.value;

        // If confidence arrived via saved search, set and reflect in UI
        if (updatedValue.hasOwnProperty('lowerConfidence') || updatedValue.hasOwnProperty('upperConfidence')) {
          const lower = updatedValue.lowerConfidence === undefined || updatedValue.lowerConfidence === null ? null : Number(updatedValue.lowerConfidence);
          const upper = updatedValue.upperConfidence === undefined || updatedValue.upperConfidence === null ? null : Number(updatedValue.upperConfidence);
          const confKey = `confidence:${foundObj.label}Aira${foundObj.airaQuestionId}`;
          this.confidenceValues[confKey] = { lower, upper };
          this.updateConfidenceSliderUI(foundObj.airaQuestionId, foundObj.label);
        }

        this.triggerConfigTextInputWithObjectUpdate(
          updatedValue.value,
          foundObj
        );
      }
    });
  }

  // Try to update the visible confidence slider UI for a given item
  // Uses `confidence:${label}Aira${airaQuestionId}` as both UI selector and map key
  updateConfidenceSliderUI(airaQuestionId: string, label: string): void {
    if (!this.confidenceSliders) return;
    const confKey = `confidence:${label}Aira${airaQuestionId}`;
    const slider = this.confidenceSliders?.find(
      confidenceSlider => confidenceSlider?.formName === confKey
    );
    const confidenceObject = this.confidenceValues[confKey];
    this.applyConfidenceUpdatesToSlider(slider, confidenceObject);
  }

  applySavedConfidenceToSliders(): void {
    if (!this.confidenceSliders || !this.confidenceValues) return;
    this.confidenceSliders.forEach(slider => {
      const key = slider?.formName;
      if (!key) return;
      const confidenceObject = this.confidenceValues[key];
      if (!confidenceObject) return;
      this.applyConfidenceUpdatesToSlider(slider, confidenceObject);


    });
  }

  applyConfidenceUpdatesToSlider(
    slider: ZintNgxSliderComponent | undefined,
    confidenceObject: { lower: number | null; upper: number | null } | undefined
  ): void {
    if (!slider || !confidenceObject) return;
    const updateObject: any = {};
    if (confidenceObject.lower !== null && confidenceObject.lower !== undefined) {
      updateObject.lowerUpdate = confidenceObject.lower;
    }
    if (confidenceObject.upper !== null && confidenceObject.upper !== undefined) {
      updateObject.upperUpdate = confidenceObject.upper;
    }
    if (Object.keys(updateObject).length) {
      slider.updateSliderValues(updateObject);
    }
  }

  checkThatBox$(option: Record<string, any>): Observable<any> {
    return of(option).pipe(
      delay(1), // 1ms delay for the change detection - mimics setTimeout(1)
      tap(opt => {
        opt.checked = true;
        opt.shouldShow = true;
      })
    );
  }

  getAllOptionsDict(): Record<string, any> {
    const manualMultiCheckboxes = {
      selectedCompanyStatuses: {
        options: this.companyStatusOptions,
        selector: 'name',
      },
      selectedUBOCountries: {
        options: this.countriesOfUBOsOptions,
        selector: 'code',
      },
      selectedCompanyCategories: {
        options: this.companyCategoryOptions,
        selector: 'name',
      },
      selectedSICCodes: {
        options: this.companySICCodeOptions,
        selector: 'identifier',
      },
      excludedSICCodes: {
        options: this.companySICCodeOptions,
        selector: 'identifier',
        attribute: 'excluded',
      },
      selectedOfficeCountries: {
        options: this.countriesOfOfficesOptions,
        selector: 'code',
      },
      selectedGeographicalRevenueCountries: {
        options: this.countriesOfGeographicalRevenueOptions,
        selector: 'code',
      },
      selectedGeographicalRevenueTerritories: {
        options: this.territoriesOfGeographicalRevenueOptions,
        selector: 'code',
      },
      selectedCountries: {
        options: this.countriesOfOriginOptions,
        selector: 'code',
      },
      selectedFunctionalDepartments: {
        options: this.functionalDepartmentOptions,
        selector: 'name',
      },
      ukRegion: {
        options: this.ukRegions,
        selector: 'name',
      },
      nationalitiesOfDirectorsSelected: {
        options: this.nationalitiesOfDirectorsOptions,
        selector: 'code',
      },
      nationalitiesOfPSCsSelected: {
        options: this.nationalitiesOfPSCsOptions,
        selector: 'code',
      },
      selectedImportExportCategories: {
        options: this.importExportCategories,
        selector: 'unique_identifier',
      },
      selectedWebTechnologies: {
        options: this.webTechnologyOptions,
        selector: 'name',
      },
      selectedFinancialYearEnds: {
        options: this.financialYearEndsOptions,
        selector: 'value',
      },
      trademarkClassIdsSelected: {
        options: this.trademarkClassOptions,
        selector: 'id',
      },
      selectedTrademarkStatuses: {
        options: this.trademarkStatusOptions,
        selector: 'id',
      },
      tagNegativeIdsSelected: {
        options: this.tagsNegativeOptions,
        selector: 'id',
      },
      tagPositiveIdsSelected: {
        options: this.tagsPositiveOptions,
        selector: 'id',
      },
      crossSellTagIdsSelected: {
        options: this.crossSellTagOptions,
        selector: 'id',
      },
      alertIdsSelected: { options: this.alertOptions, selector: 'id' },
      selectedModules: { options: this.selectedModules, selector: 'id' },
    };

    //config multiselect boxes e.g. aira
    let configMultiSelectOptions = {};

    Object.values(this.zintMultiSelectConfigs)?.forEach(sector => {
      sector?.forEach(config => {
        configMultiSelectOptions = {
          ...configMultiSelectOptions,
          [config.label]: { options: config.options, selector: 'label' },
        };
      });
    });

    return { ...manualMultiCheckboxes, ...configMultiSelectOptions };
  }

  selectActiveCompaniesInUI() {
    if (this.companyStatusOptions?.length) {
      this.companyStatusOptions
        .filter(option => option.status_clean == 'Active Companies')
        .map(option => (option.checked = true));
    }
  }

  getOptions(selectedKeyString) {
    return this.getAllOptionsDict()[selectedKeyString];
  }

  checkRequiredBoxes(key, listOfOptionsThatShouldBeSelected) {
    const parsedOptionsThatShouldBeSelected = JSON.parse(
      listOfOptionsThatShouldBeSelected
    );
    const optionsInfo = this.getOptions(key);
    const options = optionsInfo['options'] || [];
    const selector = optionsInfo['selector'];

    let filteredOpts = [];

    /**
     * edge case for hasAIRAModuleInsights checkboxes
     * they behave like multi-checkboxes but
     * they each render within individual module
     */
    if (key === 'selectedModules') {
      const idsSet = new Set(
        parsedOptionsThatShouldBeSelected.map(opt => opt.id)
      );
      filteredOpts = options.filter(option => idsSet.has(option.id));
    } else {
      filteredOpts = options.filter(option =>
        parsedOptionsThatShouldBeSelected.includes(option[selector])
      );
    }

    filteredOpts?.forEach(option => this.checkThatBox$(option).subscribe());
  }

  updateUIBasedUponDictionaryValuesReturnShouldRunAgain(queryParams): boolean {
    let shouldRunSearch = false;
    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        if (key == 'selectedCustomColumns') {
          this.updateCustomColumnsIfRequired(JSON.parse(queryParams[key]));
        } else if (key == 'selectedView') {
          this.selectedView = JSON.parse(queryParams[key]);
        } else if (key == 'websiteKeywords') {
          this.websiteKeywords = JSON.parse(queryParams[key]);
        } else if (key == 'pageNumber') {
          this.pageNumber = parseInt(queryParams[key]);
        } else if (key == 'editPropensityModelTAM') {
          this.canEditTAM = true;
        } else if (key == 'selectedEvents') {
          this.selectedEvents = JSON.parse(queryParams[key]);
        } else if (
          key === 'selectedAccountants' ||
          key === 'selectedBankers' ||
          key === 'selectedAuditors' ||
          key === 'selectedLawyers'
        ) {
          this[key] = JSON.parse(queryParams[key]);
        } else if (key == 'selectedTerritory') {
          this.selectedTerritory = JSON.parse(queryParams[key]);
          // Because we reuse companySICCodeOptions for selectedSICCodes and excludedSICCodes we need to handle them separately
        } else if (key === 'selectedSICCodes') {
          // for each selected SIC code, find the corresponding companySICCodeOption and set checked to true
          this.restoreOrStashSICCodes(JSON.parse(queryParams[key]), 'checked');
        } else if (key === 'excludedSICCodes') {
          // for each excluded SIC code, find the corresponding companySICCodeOption and set excluded to true
          this.restoreOrStashSICCodes(JSON.parse(queryParams[key]), 'excluded');
        } else if (key === 'savedSearchName') {
          this.savedSearchParamName = queryParams[key];
        } else {
          if (this[key] && Array.isArray(this[key])) {
            if (queryParams[key]) {
              this.checkRequiredBoxes(key, queryParams[key]);
            }
          } else if (this[key] !== undefined) {
            this[key] = queryParams[key];
            this.seeIfNeedToUpdateSlider(key, queryParams[key]);
          } else {
            this.seeIfNeedToUpdateSlider(key, queryParams[key]);
            this.updateNonSliderConfigFilters(key, queryParams[key]);
          }
        }
        shouldRunSearch = true;
      }
    }
    return shouldRunSearch;
  }

  // Apply restored SIC-code selections if the options have loaded, otherwise
  // stash them so applyStashedSICCodeSelections() can re-apply once
  // updateUIComponents() populates companySICCodeOptions. Guards against the
  // load-order race where param restore runs before the options land.
  restoreOrStashSICCodes(
    sicCodes: Array<string>,
    property: 'checked' | 'excluded'
  ): void {
    if (this.companySICCodeOptions?.length) {
      this.applySICCodeSelections(sicCodes, property);
    } else if (property === 'checked') {
      this.pendingSelectedSICCodes.push(...sicCodes);
    } else {
      this.pendingExcludedSICCodes.push(...sicCodes);
    }
  }

  applySICCodeSelections(
    sicCodes: Array<string>,
    property: 'checked' | 'excluded'
  ): void {
    sicCodes.forEach(sicCode => {
      const sicCodeOption = this.companySICCodeOptions.find(
        option => option.identifier === sicCode
      );
      if (sicCodeOption) {
        sicCodeOption[property] = true;
        sicCodeOption.shouldShow = true;
      }
    });
  }

  applyStashedSICCodeSelections(): void {
    if (!this.companySICCodeOptions?.length) return;
    if (this.pendingSelectedSICCodes.length) {
      this.applySICCodeSelections(this.pendingSelectedSICCodes, 'checked');
      this.pendingSelectedSICCodes = [];
    }
    if (this.pendingExcludedSICCodes.length) {
      this.applySICCodeSelections(this.pendingExcludedSICCodes, 'excluded');
      this.pendingExcludedSICCodes = [];
    }
  }

  getImpExpDataAndGetUIdata(key: string): void {
    if (key === 'selectedImportExportCategories') {
      const queryString = JSON.parse(this.urlQueryParams[key]).toString();
      this.dashboardService
        .searchImportExportCodes(queryString)
        .subscribe(searchData => {
          this.updateVisibleImportExportCodesAfterSearch(searchData);
          this.checkRequiredBoxes(key, this.urlQueryParams[key]);

          this.getAndUpdateUIData(false);
        });
    }
  }

  initialiseBasedUponGetParamsIfAllLoaded() {
    /*
      set the keyToSliderMap here after UIData loaded
      need to call this before updateUIBasedUpon...
      */
    this.mapKeyToSliderElement();

    const shouldRunSearchOnceAllParamsLoaded =
      this.updateUIBasedUponDictionaryValuesReturnShouldRunAgain(
        this.urlQueryParams
      );

    if (shouldRunSearchOnceAllParamsLoaded) {
      setTimeout(() => this.searchCompaniesAfterFilters(false), 1);
    } else {
      setTimeout(() => this.getInitialCompanies(), 1);
      this.selectActiveCompaniesInUI();
    }
  }

  updateAlerts(data): void {
    this.alertOptions = data.alerts;
    this.teamMembers = data.team_members;
    if (data.team_members && data.team_members.length > 0) {
      this.teamMemberIdToCreateAlertFor = data.team_members[0].id;
    }
    this.hasLoadedFlags.hasLoadedAlertOptions.set(true);
  }

  updateCustomColumnsIfRequired(newCustomColumns): void {
    // Here we need to update the custom columns as selected, e.g. specific values etc.
    // We need to pull the options from the options that are shared in the UI, not through the URL though as if from a saved
    // search then it will likely be out of date.
    let output = [];
    this.customColumns.forEach(col => {
      let matchingColumns = newCustomColumns.filter(_col => col.id == _col.id);
      if (matchingColumns && matchingColumns.length > 0) {
        let matchingColumn = matchingColumns[0];
        if (col.options) {
          let optionsOutput = [];
          col.options.forEach(col_option => {
            let matchingOptions = matchingColumn.options.filter(
              _option => col_option.id == _option.id
            );
            if (matchingOptions && matchingOptions.length > 0) {
              let matchingOption = matchingOptions[0];
              optionsOutput.push(matchingOption);
            } else {
              optionsOutput.push(col_option);
            }
          });
          matchingColumn.options = optionsOutput;
        }
        // Update permissions from current columns
        matchingColumn.can_edit = col.can_edit;
        matchingColumn.can_view = col.can_view;
        output.push(matchingColumn);
      } else {
        output.push(col);
      }
    });
    this.customColumns = output;
    this.updateFilteredCustomColumns();
  }

  updateCustomColumnsAndColumnWidths(output): void {
    this.customColumnNamesToObjects = {};
    this.customColumns = output.custom_columns;
    this.teamMembersForCustomColumns = output.team_members;
    if (this.customColumns) {
      this.customColumns.forEach(col => {
        this.customColumnNamesToObjects[col.name] = col;
        if (col.column_type == 'User') {
          let copyOfTeamMembersToAssign = JSON.parse(
            JSON.stringify(this.teamMembersForCustomColumns)
          );
          col.options = copyOfTeamMembersToAssign;
        }
      });
    }
    this.updateFilteredCustomColumns();
    this.hasLoadedFlags.hasLoadedCustomColumns.set(true);
    if (output.column_widths) {
      this.columnNamesToWidths = output.column_widths;
    } else {
      this.columnNamesToWidths = { 'Company Name': 350 };
    }
  }

  clearAllCurrentlyEditingAndUpdateValue(updatedValue, company_k_v): void {
    company_k_v.val = updatedValue;
    this.currentlyEditingRowName = '';
    this.currentlyEditingColumnName = '';
  }

  updateCurrentlyEditingCols(row_id, col_name): void {
    if (this.customColumnNamesToObjects[col_name]) {
      if (this.customColumnNamesToObjects[col_name]['can_edit']) {
        this.currentlyEditingRowName = row_id;
        this.currentlyEditingColumnName = col_name;
      }
    }
  }

  toggleProfilePreview(companyNumber?: string): void {
    if (this.isScreenTooSmall()) {
      return;
    }

    const currentCompanyNumber = this.dataStore.companyNumberFromParent();
    const isSameCompany = currentCompanyNumber === companyNumber;

    // if no company provided (close button)
    if (!companyNumber) {
      this.profilePreviewIsOpen.set(false);
      this.dataStore.companyNumberFromParent.set('');
      return;
    }

    // For same company, do nothing.
    if (isSameCompany) {
      return;
    }
    this.dataStore.companyNumberFromParent.set('');
    this.cdr.detectChanges();
    this.dataStore.companyNumberFromParent.set(companyNumber);

    this.profilePreviewIsOpen.set(true);

    // Collapse sidebar if needed
    if (!this.isSidebarCollapsed()) {
      this.expandOrCollapseSidebar();
    }
  }

  triggerChangeDetection(event: any) {
    this.cdr.markForCheck();
  }

  drawerStateChanged(isOpen: boolean): void {
    this.profilePreviewIsOpen.set(isOpen);
    if (!isOpen) {
      this.dataStore.companyNumberFromParent.set('');
    }
    this.cdr.markForCheck();
  }

  updateCrossSellTagOptions(output): void {
    this.crossSellTagOptions = output;
    this.hasLoadedFlags.hasLoadedUserCrossSellTags.set(true);
  }

  updateTagOptions(output): void {
    if (output.user_tags) {
      this.tagsPositiveOptions = output.user_tags.map(tag => ({ ...tag, shouldShow: true }));
      const copy = JSON.parse(JSON.stringify(output.user_tags));
      // otherwise we end up with the same data binding!
      this.tagsNegativeOptions = copy.map(tag => ({ ...tag, shouldShow: true }));
      this.hasLoadedFlags.hasLoadedUserTags.set(true);
    }
  }

  updateScrollBarDimensions() {
    $(document).ready(function () {
      $('.dummy-top-scrollbar').width(
        $('#table-results-section').find('table').width()
      );
      $('.dummy-top-scrollbar-wrapper').width(
        $('#table-results-section').width() + 15
      );
    });
  }

  updateCompaniesAndRenderInfo(output, isInitial) {
    this.companies = output;
    this.pubMethods.allowDraggingOftable('.company-results-table');
    this.updateScrollBarDimensions();
    if (!isInitial) {
      this.updateCompanyIfInaccurateEstimate();
    }
  }
  getInitialCompanies(): void {
    this.dashboardService
      .getInitialCompanies()
      .subscribe(output => this.updateCompaniesAndRenderInfo(output, true));
  }

  getAndUpdateUIData(isDefaultImpExp = true) {
    this.dashboardService.getUIData().subscribe(data => {
      this.updateUIComponents(data);
      if (isDefaultImpExp) {
        this.importExportCategories = data.import_export_sections;
      }
    });
  }

  getUIDataAndUpdateCompanies(): void {
    if (
      this.route.snapshot.queryParamMap.has('selectedImportExportCategories')
    ) {
      this.getImpExpDataAndGetUIdata('selectedImportExportCategories');
    } else {
      this.getAndUpdateUIData();
    }
  }

  searchWebTechs(): void {
    if (this.webTechsTextSearch && this.webTechsTextSearch.length > 2) {
      this.isSearchingWebTechs = true;
      this.selectedWebTechnologies = this.webTechnologyOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));
      this.selectedWebTechnologies = this.webTechnologyOptions
        .filter(
          option =>
            option.description
              .toLowerCase()
              .indexOf(this.webTechsTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));
    }
  }

  resetWebTechs(): void {
    this.selectedWebTechnologies = this.webTechnologyOptions.map(
      option => (option.shouldShow = false)
    );
    this.selectedWebTechnologies = this.webTechnologyOptions
      .filter(option => option.level === 0)
      .map(option => (option.shouldShow = true));
    this.isSearchingWebTechs = false;
  }

  searchSicCodes(): void {
    if (!this.sicCodeTextSearch?.length) {
      this.resetSicSearch();
    }

    if (this.sicCodeTextSearch && this.sicCodeTextSearch.length > 2) {
      this.isSearchingSicCodes = true;
      this.selectedSICCodes = this.companySICCodeOptions.map(
        option => (option.shouldShow = false)
      );
      this.selectedSICCodes = this.companySICCodeOptions
        .filter(option => {
          if (isNaN(Number(this.sicCodeTextSearch))) {
            return (
              option.description
                .toLowerCase()
                .indexOf(this.sicCodeTextSearch.toLowerCase()) >= 0
            );
          } else {
            // actual sic code number search e.g. 01230 Growing citrus
            return option.identifier.indexOf(this.sicCodeTextSearch) >= 0;
          }
        })
        .map(option => (option.shouldShow = true));
    }
  }

  resetSicSearch(): void {
    this.selectedSICCodes = this.companySICCodeOptions
      .filter(option => (option.shouldShow = true))
      .map(option => (option.shouldShow = false));
    this.isSearchingSicCodes = false;
    this.sicCodeTextSearch = '';
  }

  updateFilters(): void {
    //TODO: check if we can early return without optional chain everything
    this.selectedUBOCountries = this.countriesOfUBOsOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.nationalitiesOfDirectorsSelected = this.nationalitiesOfDirectorsOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.nationalitiesOfPSCsSelected = this.nationalitiesOfPSCsOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.selectedWebTechnologies = this.webTechnologyOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.selectedImportExportCategories = this.importExportCategories
      .filter(option => option.checked)
      .map(option => option.unique_identifier);

    this.selectedCompanyStatuses = this.companyStatusOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.selectedCountries = this.countriesOfOriginOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.selectedOfficeCountries = this.countriesOfOfficesOptions
      .filter(option => option.checked)
      .map(option => option.code);

    this.selectedGeographicalRevenueCountries =
      this.countriesOfGeographicalRevenueOptions
        .filter(option => option.checked)
        .map(option => option.code);

    this.selectedGeographicalRevenueTerritories =
      this.territoriesOfGeographicalRevenueOptions
        .filter(option => option.checked)
        .map(option => option.code);

    this.selectedFunctionalDepartments = this.functionalDepartmentOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.ukRegion = this.ukRegions
      .filter(option => option.checked)
      .map(option => option.name);

    this.selectedSICCodes = this.companySICCodeOptions
      .filter(option => option.checked)
      .map(option => option.identifier);

    this.excludedSICCodes = this.companySICCodeOptions
      .filter(option => option.excluded)
      .map(option => option.identifier);

    //noinspection TypeScriptUnresolvedVariable
    this.selectedCompanyCategories = this.companyCategoryOptions
      .filter(option => option.checked)
      .map(option => option.value);

    //noinspection TypeScriptUnresolvedFunction
    this.selectedFinancialYearEnds = this.financialYearEndsOptions
      .filter(option => option.checked)
      .map(option => option.value);

    this.tagPositiveIdsSelected = this.tagsPositiveOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.tagNegativeIdsSelected = this.tagsNegativeOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.crossSellTagIdsSelected = this.crossSellTagOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.trademarkClassIdsSelected = this.trademarkClassOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.selectedTrademarkStatuses = this.trademarkStatusOptions
      .filter(option => option.checked)
      .map(option => option.name);

    this.newsEventCategories = this.newsEventCategoryGroups.flatMap(group =>
      group.subcategories.filter(sub => sub.checked).map(sub => sub.name)
    );

    this.alertIdsSelected = this.alertOptions
      .filter(option => option.checked)
      .map(option => option.id);

    this.selectedCustomColumns = this.getSelectedCustomColumns();
  }

  getSelectedCustomColumns(): Array<any> {
    const outPutColumns = this.customColumns?.filter(
      column =>
        (column.dropdownInput &&
          (column.dropdownInput == 'isSet' ||
            column.dropdownInput == 'isNotSet' ||
            column.dropdownInput == 'isMe' ||
            column.dropdownInput == 'lockState' ||
            column.boxInput ||
            column.boxInput == 0 ||
            column.boxInputLower ||
            column.boxInputLower == 0 ||
            column.boxInputUpper ||
            column.boxInputUpper == 0)) ||
        column.options.filter(option => option.checked).length > 0
    );
    // remove the attributes that we do not need that clog up the URL params
    outPutColumns?.forEach(col => delete col.edit_users);
    outPutColumns?.forEach(col => delete col.view_users);
    return outPutColumns;
  }

  // Create HttpParams from current state of filters
  allFilterParams(): HttpParams {
    return this.dashboardService.getFilterParameters(
      this.selectedTerritory,
      this.companyNameContains,
      this.accountantNameContains,
      this.bankerNameContains,
      this.auditorNameContains,
      this.lawyerNameContains,
      this.selectedAccountants,
      this.selectedBankers,
      this.selectedAuditors,
      this.selectedLawyers,
      this.selectedCompanyStatuses,
      this.lowerNumberOfUBOs,
      this.upperNumberOfUBOs,
      this.selectedUBOCountries,
      this.selectedCountries,
      this.selectedCompanyCategories,
      this.selectedSICCodes,
      this.excludedSICCodes,
      this.websiteKeywords,
      this.corporateStructureIsTopLevelParentCompany,
      this.corporateStructureHasParent,
      this.corporateStructureDoesNotHaveParent,
      this.corporateStructureHasChild,
      this.corporateStructureDoesNotHaveChild,
      this.corporateStructureRelatedCompanies,
      this.hasWebsite,
      this.employeeLowerCount,
      this.employeeUpperCount,
      this.excludeEstimatedHeadcountFigures,
      this.lowerHeadCountGrowthPercentage,
      this.upperHeadCountGrowthPercentage,
      this.departmentHeadCountLowerCount,
      this.departmentHeadCountUpperCount,
      this.selectedFunctionalDepartments,
      this.lowerAge,
      this.upperAge,
      this.selectedAddressTypeOption,
      this.postcodeBeginsWith,
      this.postcodeDistrict,
      this.ukRegion,
      this.postcodeWithinRadius,
      this.selectedTownId,
      this.mileRadiusOfPostcode,
      this.hasOverseasParent,
      this.hasOfficesOverseas,
      this.selectedOfficeCountries,
      this.lowerNumberOfKnownOfficeLocations,
      this.upperNumberOfKnownOfficeLocations,
      this.selectedGeographicalRevenueCountries,
      this.selectedGeographicalRevenueTerritories,
      this.lowerGeographicalRevenueSelected,
      this.upperGeographicalRevenueSelected,
      this.hasLEINumber,
      this.selectedLEIStatus,
      this.selectedImportExportCategories,
      this.selectedImportingOrExportingOption,
      this.importExportTimeScale,
      this.importExportFrequencyImportingOrExporting,
      this.importExportFrequency,
      this.importExportFrequencyMonthCount,
      this.selectedWebTechnologies,
      this.webTrafficLow,
      this.webTrafficHigh,
      this.homepageSearchTerm,
      this.fullWebsiteSearchTerm,
      this.lowerAverageDirectorAgeRange,
      this.upperAverageDirectorAgeRange,
      this.lowerNumberOfDirectors,
      this.upperNumberOfDirectors,
      this.lowerNumberOfPSCs,
      this.upperNumberOfPSCs,
      this.PSCsRealPersonsOnly,
      this.recentDirectorshipTitleContains,
      this.lowerRecentDirectorshipMonth,
      this.upperRecentDirectorshipMonth,
      this.nationalitiesOfDirectorsSelected,
      this.nationalitiesOfPSCsSelected,
      this.lowerDaysUntilFinancialYearEnd,
      this.upperDaysUntilFinancialYearEnd,
      this.selectedFinancialYearEnds,
      this.lowerDaysUntilAnniversary,
      this.upperDaysUntilAnniversary,
      this.isHiring,
      this.hiringJobTitleContains,
      this.hiringDescriptionContains,
      this.selectedHiringDayLimit,
      this.lowerNumberOfRolesHiringFor,
      this.upperNumberOfRolesHiringFor,
      this.selectedNumberOfRolesHiringForDayLimit,
      this.includeRecruiters,
      this.directorJobTitleContains,
      this.amazonSeller,
      this.listedOnGCloud,
      this.hasPhoneNumber,
      this.hasEmailAddress,
      this.hasRaisedInvestment,
      this.hasShareFilingEvent,
      this.shareFilingEventWithinDays,
      this.lowerShareFilingCapitalValue,
      this.upperShareFilingCapitalValue,
      this.lowerInvestmentAmount,
      this.upperInvestmentAmount,
      this.ignoreRevenueEstimates,
      this.lowerAccountsDueSelected,
      this.upperAccountsDueSelected,
      this.lowerLatestAccountsSubmittedSelected,
      this.upperLatestAccountsSubmittedSelected,
      this.selectedLatestAccountsGroupOption,
      this.selectedLatestAccountsAuditOption,
      this.accountsAreOverdue,
      this.accountsSearchTerm,
      this.accountsSearchWithinDays,
      this.issuedAGrant,
      this.lowerGrantAmount,
      this.upperGrantAmount,
      this.grantLowerMonthsUntilProjectEnd,
      this.grantUpperMonthsUntilProjectEnd,
      this.chargeRegistered,
      this.outstandingChargeRegistered,
      this.noOutstandingCharges,
      this.selectedEvents,
      this.postcodeEventsWithinRadius,
      this.mileRadiusOfEventsSelected,
      this.eventsWithinDaysLower,
      this.eventsWithinDaysUpper,
      this.newsWithinDays,
      this.newsContaining,
      this.newsSource,
      this.newsEventCategories,
      this.trademarkClassIdsSelected,
      this.trademarkDescriptionContains,
      this.trademarkNameContains,
      this.selectedTrademarkStatuses,
      this.excludeCompaniesTaggedByTeam,
      this.tagPositiveIdsSelected,
      this.tagNegativeIdsSelected,
      this.crossSellTagIdsSelected,
      this.crossSellLinkedThroughPeople,
      this.crossSellLinkedThroughCorporateStructure,
      this.similarCompanyUrl,
      this.pageNumber,
      this.sortKey,
      this.sortDirectionIsAscending,
      this.selectedCustomColumns,
      this.unreadAlertsOnly,
      this.lowerAlertsDayRange,
      this.upperAlertsDayRange,
      this.alertIdsSelected,
      this.selectedView,
      this.isLowEnergy,
      this.lowerTotalEnergyKwhSelected,
      this.upperTotalEnergyKwhSelected,
      this.lowerGasKwhSelected,
      this.upperGasKwhSelected,
      this.lowerElectricKwhSelected,
      this.upperElectricKwhSelected,
      this.lowerScope1KwhSelected,
      this.upperScope1KwhSelected,
      this.lowerScope2KwhSelected,
      this.upperScope2KwhSelected,
      this.lowerScope3KwhSelected,
      this.upperScope3KwhSelected,
      this.lowerTotalEmissionsKgCo2eSelected,
      this.upperTotalEmissionsKgCo2eSelected,
      this.lowerScope1KgCo2eSelected,
      this.upperScope1KgCo2eSelected,
      this.lowerScope2KgCo2eSelected,
      this.upperScope2KgCo2eSelected,
      this.lowerScope3KgCo2eSelected,
      this.upperScope3KgCo2eSelected,
      this.lowerKgCo2ePerMillionRevenueSelected,
      this.upperKgCo2ePerMillionRevenueSelected,
      this.lowerKgCo2ePerEmployeeSelected,
      this.upperKgCo2ePerEmployeeSelected,
      this.hasActivePatent,
      this.hasAnyPatentRecord,
      this.patentFiledWithinDays,
      this.lowerPatentAwardedDays,
      this.upperPatentAwardedDays,
      this.hasContract,
      this.lowerContractValueSelected,
      this.upperContractValueSelected,
      this.contractAwardedWithinMonths,
      this.lowerContractStartMonthsSelected,
      this.upperContractStartMonthsSelected,
      this.lowerContractEndMonthsSelected,
      this.upperContractEndMonthsSelected,
      this.contractTitleContains,
      this.contractDescriptionContains,
      this.ignoreSatisfiedCharges,
      this.chargeHolderNameContains,
      this.chargeCreatedMonthsAgoLower,
      this.chargeCreatedMonthsAgoUpper,
      this.supplierTagSelected,
      this.associateWithOneSupplierOnly,
      this.customerTagSelected,
      this.summarySemanticSearchTerm,
      this.summarySemanticSearchConceptMatch,
      this.summarySemanticSearchMinScore,
      this.clusterSearchMode,
      this.sliderValues,
      this.configTextInputValues,
      this.configMultiSelectValues,
      this.selectedPageSizeLimit,
      this.selectedModules,
      this.hasLandTitle,
      this.landTenure,
      this.landCategory
    );
  }

  definedPage(page: number | string): void {
    if (typeof page === 'string') {
      page = Math.abs(parseInt(page));
      if (isNaN(page) || page < 1) {
        this.messageService.showAndHideAfterDelay(
          'Enter positive numbers only!',
          3100
        );
        return;
      }
    }

    if (typeof page === 'number' && page <= this.maxPageNumber) {
      this.pageNumber = page;
      this.searchCompaniesAfterFiltersNoCount();
    }
  }

  nextPage(): void {
    if (this.pageNumber != this.maxPageNumber) {
      this.pageNumber += 1;
      this.searchCompaniesAfterFiltersNoCount();
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber -= 1;
      this.searchCompaniesAfterFiltersNoCount();
    }
  }

  sortBy(sortKey): void {
    if (this.sortKey == sortKey) {
      if (!this.sortDirectionIsAscending) {
        this.sortDirectionIsAscending = true;
      } else {
        this.sortKey = null;
        this.sortDirectionIsAscending = false;
      }
    } else {
      this.sortKey = sortKey;
      this.sortDirectionIsAscending = false;
    }
    this.searchCompaniesAfterFilters(true);
  }

  // ZIN-5643: when a P&S semantic term is active, default the result sort to
  // match score (descending) so the closest matches surface first. Only
  // applied when the user has no explicit sort, so a manual column sort still
  // wins; when the term is cleared the auto sort is dropped so the backend
  // default order returns.
  enforceSemanticScoreSortDefault(): void {
    const hasSemanticTerm = !!(this.summarySemanticSearchTerm || '').trim();
    if (hasSemanticTerm && !this.sortKey) {
      this.sortKey = this.semanticSearchScoreSortKey;
      this.sortDirectionIsAscending = false;
    } else if (
      !hasSemanticTerm &&
      this.sortKey === this.semanticSearchScoreSortKey
    ) {
      this.sortKey = '';
      this.sortDirectionIsAscending = false;
    }
  }

  // ZIN-5642: the reused zint-ngx-slider defaults its single thumb to max, so
  // seed it to the current score floor whenever the slider (re)appears. The
  // control is inside an *ngIf, so a setter ViewChild is the reliable hook.
  @ViewChild('semanticMatchQualitySliderElement')
  set semanticMatchQualitySliderElement(slider: ZintNgxSliderComponent) {
    if (slider) {
      setTimeout(
        () =>
          slider.updateSliderValues({
            upperUpdate: this.summarySemanticSearchMinScore,
          }),
        0
      );
    }
  }

  // ZIN-5642: slider handler - single-handle sliders emit the value as `upper`.
  // Like every other slider, this only updates state; the search runs when the
  // user hits Search (and the usual triggers), not on each slider move.
  updateSemanticMatchScore(sliderEvent: any): void {
    this.summarySemanticSearchMinScore = sliderEvent?.upper ?? 0;
  }

  clearSearchParam(paramObj): void {
    if (this[paramObj['param']] !== undefined) {
      this[paramObj['param']] = null;
    } else {
      this.clearSearchForConfigDrivenFilters(paramObj);
    }

    if (paramObj['param'] === 'selectedCustomColumns') {
      this.customColumns.map(column =>
        column.options.map(option => (option.checked = false))
      );
      this.customColumns.map(column => (column.dropdownInput = ''));
      this.customColumns.map(column => (column.lockFilter = ''));
    }

    if (paramObj['param'] === 'selectedModules') {
      this.setHasAIRAModulesEnabledOptions();
    }

    const oldParams = this.allFilterParams()['updates'];

    const getFormatOldParams = {};
    for (const oldParam of oldParams) {
      if (
        oldParam.value !== '' &&
        oldParam.value !== 'null' &&
        oldParam.value !== 'false' &&
        oldParam.value !== null
      ) {
        getFormatOldParams[oldParam.param] = oldParam.value;
      }
    }

    this.resetSpecificFilter(paramObj.param);

    this.updateUIBasedUponDictionaryValuesReturnShouldRunAgain(
      getFormatOldParams
    );

    this.searchTags = JSON.parse(JSON.stringify(oldParams)); // seem to need to do this otherwise it references the old filter parameters?
    this.getActiveSearchParams();
  }

  private clearConfidenceForFilter(paramValue: any, label: string): void {
    if (!this.pubMethods.isValidJson(paramValue)) {
      return;
    }
    try {
      const parsed = JSON.parse(paramValue);
      const airaId = parsed?.airaQuestionId;
      if (airaId) {
        const confKey = `confidence:${label}Aira${airaId}`;
        delete this.confidenceValues[confKey];
        const slider = this.confidenceSliders?.find(
          s => s?.formName === confKey
        );
        slider?.reset();
      }
    } catch (e) {
      // Ignore if parsing fails, it's not a filter object with confidence.
    }
  }

  clearSearchForConfigDrivenFilters(paramObj: Record<string, any>): void {
    const paramString: string = paramObj.param;
    let filterType = '';
    if (this.pubMethods.isValidJson(paramObj.value)) {
      try {
        filterType = JSON.parse(paramObj.value).filterType;
      } catch (e) {
        // Not a JSON object with a filterType, so we can ignore.
      }
    }

    if (paramString.startsWith('lower') || paramString.startsWith('upper')) {
      // Config sliders (e.g., for numeric ranges)
      const propsKey = paramString.slice(0, 5); // 'lower' or 'upper'
      const sliderLabel = paramObj.param.slice(5);
      if (this.sliderValues[sliderLabel]) {
        this.sliderValues[sliderLabel][propsKey] = null;
      }
      this.clearConfidenceForFilter(paramObj.value, sliderLabel);
    } else if (filterType === 'multi_select') {
      this.configMultiSelectValues[paramString] = null;
      this.clearConfidenceForFilter(paramObj.value, paramString);
    } else if (filterType === 'text_input') {
      this.configTextInputValues[paramString] = null;
      this.clearConfidenceForFilter(paramObj.value, paramString);
    }
  }

  searchCompaniesAfterFiltersNoCount(): void {
    this.enforceSemanticScoreSortDefault();
    this.updateFilters();
    const params = this.allFilterParams();
    this.searchTags = JSON.parse(JSON.stringify(params)).updates;
    this.getActiveSearchParams();
    this.checkboxSelectAllCompanies = false;
    this.showOrHideInfoOnAllSelectedCompanies();
    this.dashboardService
      .searchCompaniesNew(params)
      .subscribe(output => this.updateCompaniesAndRenderInfo(output, false));
  }

  getActiveSearchParams(): void {
    const currentActiveParamStrings = this.searchTags
      .filter(
        ({ value, param }) =>
          value &&
          value !== 'false' &&
          value !== 'null' &&
          param !== 'pageNumber'
      )
      .map(({ param }) => {
        // remove spaces
        return param.replace(/\s+/g, '');
      });

    const cardsToParams = { ...collapseCardToParamsMappings };
    Object.keys(this.linkedAiraFilters).forEach(key => {
      const objKey = this.removeSpecialCharsAndSpaces(key) + '-collapse-card';
      cardsToParams[objKey] = [...magicFilterParams];
    });

    this.activeParamsFromMapper = Object.entries(cardsToParams)
      .filter(([_, filterParams]) => {
        return currentActiveParamStrings.some(activeParam =>
          filterParams.includes(activeParam)
        );
      })
      .map(([cardId]) => cardId);
  }

  highlightFilterSection(cardElementId?: string): boolean {
    if (!cardElementId) return false;
    return this.activeParamsFromMapper.includes(cardElementId);
  }

  searchCompaniesAfterFilters(initialisePageCounter: boolean): void {
    if (initialisePageCounter) {
      this.pageNumber = 1;
    }
    this.searchCompaniesAfterFiltersNoCount();
    this.getCompanyCount(false);

    //close the preview if open
    this.previewDrawer?.close();

    //clear profile data caches
    this.dataStore.clearProfileDataCaches();

    //hide filters panel if smaller screens
    if (this.isScreenTooSmall()) {
      this.toggleSidebarHeight();
    }
  }

  testIfSearchCompaniesOnEnterPress(event): void {
    if (!this.isLoading().loadingCompanies) {
      this.searchCompaniesAfterFilters(true);
    }
  }

  // When you have accts/website terms, the estimated number is sometimes v low, but you may get back a higher number than that.
  // Sometimes it is too high (but still less than 1 full page), and then you need to chop it down to the smaller number.
  // Update to that number.
  updateCompanyIfInaccurateEstimate(): void {
    if (
      !this.isLoading().loadingCompanies &&
      !this.isLoading().loadingCompanyCount &&
      this.pageNumber == 1 &&
      this.companyCount().exact == false
    ) {
      if (
        this.companies.length > this.companyCount().count ||
        (this.companies.length < this.companyCount().count &&
          this.selectedPageSizeLimit !== this.companies.length)
      ) {
        this.companyCount().count = this.companies.length;
      }
    }
  }

  updateCompanyCountAndPaginationLimits(data, isInitial): void {
    this.companyCount.set(data);
    const companiesPerPage = data['companies_per_page'];
    this.maxPageNumber = Math.ceil(
      this.companyCount().count / companiesPerPage
    );
    if (!isInitial) {
      this.updateCompanyIfInaccurateEstimate();
    }
  }

  getCompanyCount(isInitial): void {
    if (isInitial) {
      this.dashboardService
        .getInitialCompanyCount()
        .subscribe(data =>
          this.updateCompanyCountAndPaginationLimits(data, isInitial)
        );
    } else {
      this.dashboardService
        .getCompanyCount(this.allFilterParams())
        .subscribe(data =>
          this.updateCompanyCountAndPaginationLimits(data, isInitial)
        );
    }
  }

  updateUIComponents(data): void {
    this.countriesOfUBOsOptions = data.countries_of_ubos;
    this.numberUBOsRange = data.number_ubos_range;
    this.countriesOfOriginOptions = data.countries_of_origin;
    this.countriesOfOfficesOptions = data.countries_of_offices;
    this.countriesOfGeographicalRevenueOptions =
      data.countries_of_geographical_revenue;
    this.territoriesOfGeographicalRevenueOptions =
      data.territories_of_geographical_revenue;
    this.geographicalRevenueOptions = data.geographical_revenue_options;
    this.ukRegions = (data.uk_regions ?? []).map(region => ({ name: region, checked: false }));
    this.companyCategoryOptions = data.company_categories;
    this.companySICCodeOptions = data.sic_codes;
    // Re-apply any SIC-code selections that were restored before the options
    // had loaded (ZINT-ANGULARAPP-2AY).
    this.applyStashedSICCodeSelections();
    this.companyStatusOptions = data.company_statuses;
    this.numberOfKnownOfficeLocationsRange =
      data.number_of_known_locations_range;
    this.employeeRangeFigures = data.employee_bandings;
    this.headcountGrowthPercentageRange = data.headcount_growth_limits;
    this.functionalDepartmentOptions = data.functional_departments;
    this.companyAgeRange = data.company_age_range;
    this.LEIStatusOptions = data.lei_status_options;
    this.importingExportingBothOptions = data.importing_or_exporting;
    this.importExportMonthRange = data.import_export_month_range;
    this.importExportFrequencyRange = data.import_export_frequency_range;
    this.importExportFrequencyMonthCountRange =
      data.import_export_frequency_month_count_range;
    this.webTechnologyOptions = data.web_technologies;
    this.webTrafficOptions = data.web_traffic_intervals;
    this.averageDirectorAgeRange = data.average_director_age_range;
    this.numberOfDirectorsRange = data.number_of_directors_range;
    this.numberOfPSCsRange = data.number_of_pscs_range;
    this.recentDirectorshipMonthRange = data.recent_directorship_month_range;
    this.nationalitiesOfDirectorsOptions = data.nationalities_of_directors;
    this.nationalitiesOfPSCsOptions = data.nationalities_of_pscs;
    this.daysUntilFinancialYearEndLimits = data.days_until_financial_year_end;
    this.financialYearEndsOptions = data.financial_year_month_options;
    this.daysUntilAnniversaryLimits = data.days_until_company_anniversary;
    this.hiringDayLimits = data.hiring_day_limits;
    this.numberOfRolesHiringForLimits = data.number_of_roles_hiring_for_limits;
    this.numberOfRolesHiringForDayLimits =
      data.number_of_roles_hiring_for_day_limits;
    this.investmentRaisedOptions = data.investment_raised_intervals;
    this.shareFilingEventWithinDaysOptions =
      data.share_filing_event_within_days_options;
    this.shareFilingCapitalValueOptions =
      data.share_filing_capital_value_options;
    this.accountsDueInRange = data.accounts_due_in_range;
    this.latestAccountsSubmittedRange = data.latest_accounts_submitted_range;
    this.accountGroupOptions = data.account_group_options;
    this.accountAuditOptions = data.account_audit_options;
    this.grantRangeOptions = data.grant_amount_range;
    this.grantMonthsUntilProjectEndRange =
      data.grant_months_until_project_end_range;
    this.mileRadiusRegisteredAddressRange =
      data.mile_radius_registered_address_range;
    this.mileRadiusEventsRange = data.mile_radius_events_range;
    this.eventDaysRange = data.events_days_range;
    this.addressOptions = data.address_options;
    this.newsDaysRange = data.news_events_day_range;
    this.accountsSearchDayRange = data.accounts_search_day_range;
    this.trademarkClassOptions = data.trademark_class_options;
    this.trademarkStatusOptions = data.trademark_status_options;
    this.totalEnergyKwhOptions = data.total_energy_kwh_options;
    this.gasKwhOptions = data.gas_kwh_options;
    this.electricKwhOptions = data.electric_kwh_options;
    this.scope1KwhOptions = data.scope_1_kwh_options;
    this.scope2KwhOptions = data.scope_2_kwh_options;
    this.scope3KwhOptions = data.scope_3_kwh_options;
    this.totalEmissionsKgCo2eOptions = data.total_emissions_kg_co2e_options;
    this.scope1KgCo2eOptions = data.scope_1_kg_co2e_options;
    this.scope2KgCo2eOptions = data.scope_2_kg_co2e_options;
    this.scope3KgCo2eOptions = data.scope_3_kg_co2e_options;
    this.kgCo2ePerMillionRevenueOptions =
      data.kg_co2e_per_million_revenue_options;
    this.kgCo2ePerEmployeeOptions = data.kg_co2e_per_employee_options;
    this.patentFiledWithinDaysOptions = data.patent_filed_within_days_options;
    this.patentAwardedWithinDaysOptions = data.patent_filed_within_days_options;
    this.contractValueOptions = data.contract_value_options;
    this.contractAwardedWithinMonthsOptions =
      data.contract_awarded_within_months_options;
    this.contractStartMonthsOptions = data.contract_start_months_options;
    this.contractEndMonthsOptions = data.contract_end_months_options;
    this.chargeCreatedMonthLimits = data.charge_created_month_limits;

    this.alertFrequencyOptions = data.alert_frequency_options;
    this.propensityWeightingRange = data.propensity_weighting_range;
    this.propensityTAMLimitCount = data.propensity_tam_org_limit;

    this.jsonParamKeysNotAllowedForAlertsOrPropensityScore =
      data.json_param_keys_not_allowed_for_alerts;
    if (this.alertFrequencyOptions && this.alertFrequencyOptions.length) {
      this.selectedAlertFrequency = this.alertFrequencyOptions[0].identifier;
    }
    this.alertsDaysRange = data.alerts_days_range;
    this.companyColumnsNotAllowedToFilter =
      data.company_columns_not_allowed_to_filter;

    this.hasLoadedFlags.hasLoadedUIData.set(true);

    this.allConfigSlidersChildren = [
      ...this.tanAssetsSliders,
      ...this.intanAssetsSliders,
      ...this.heatScoreSliders,
      ...this.fleetSliders,
      ...this.wagesSliders,
      ...this.numberOfActiveTrademarksSliders,
      ...this.genericConfigSliders,
    ];

    this.enableFixedHeaders();
    this.layoutForMobile();
    this.applyPendingSliderUpdates();
  }

  ngAfterViewInit(): void {
    this.genericConfigSliders?.changes.subscribe(() => {
      this.applyPendingSliderUpdates();
    });

    // One more pass after initial view stabilizes
    setTimeout(() => {
      this.applyPendingSliderUpdates();
    });

    // Closing without submitting must not leave a preset company count behind for
    // the next time Get insights is opened from a different entry point.
    $('#dashboardGetInsightsModal').on('hidden.bs.modal', () => {
      this.resetMagicInsightsUnlockPreset();
    });
  }

  updateToDefaultImportingOrExporting(): void {
    if (!this.selectedImportingOrExportingOption) {
      this.selectedImportingOrExportingOption =
        this.importingExportingBothOptions[
          this.importingExportingBothOptions.length - 1
        ]['identifier'];
    }
  }

  updateImportExportFrequencyToDefaultImportingOrExporting(): void {
    if (!this.importExportFrequencyImportingOrExporting) {
      this.importExportFrequencyImportingOrExporting =
        this.importingExportingBothOptions[
          this.importingExportingBothOptions.length - 1
        ]['identifier'];
    }
  }

  getJSONParamsToSave(hideOrderingAndPageNumbers) {
    const paramsToSave = this.searchTags.filter(
      param =>
        param.value != '' &&
        param.value != null &&
        param.value != 'false' &&
        param.value != 'null'
    );
    const requiredOutput = {};
    for (const theParam of paramsToSave) {
      if (
        (hideOrderingAndPageNumbers &&
          this.jsonParamKeysNotAllowedForAlertsOrPropensityScore.indexOf(
            theParam.param
          ) < 0) ||
        !hideOrderingAndPageNumbers
      ) {
        requiredOutput[theParam.param] = theParam.value;
      }
    }
    return JSON.stringify(requiredOutput);
  }

  openResultsActionModal(resultAction: string): void {
    this.resultsActionModalTitle = resultAction;

    const modalElement = document.getElementById('searchResultsActionsModal');
    if (modalElement) {
      $(modalElement).modal('show');
    }
  }

  openCRMExportModal(crmName = 'hubspot'): void {
    if (!this.hasSelectedTotalNumberMatchingFilter) {
      this.showCRMExportModal(crmName);
      return;
    }

    this.crmService
      .getCRMExistingState({
        actionType: 'crm_object_type',
        crmName: crmName.toLowerCase(),
      })
      .subscribe((objectData) => {
        const maxRecords = objectData[0]?.maxRecordsPerRequest ?? 1000;
        if (this.companyCount().count > maxRecords) {
          this.messageService.show(
            `Maximum number of selected companies to export must not exceed ${maxRecords}.`
          );
          return;
        }
        this.showCRMExportModal(crmName);
      });
  }

  private showCRMExportModal(crmName: string): void {
    const modalRef = $('#exportToCRMModal').modal({
      backdrop: 'static', // backdrop click doesn't dismiss
      keyboard: true,
      show: false,
    });

    this.crmExporterName = crmName;
    this.cdr.detectChanges();

    const isLoadingData = this.crmService.wizardLoading().isLoadingExporter;
    if (isLoadingData) {
      modalRef.data('bs.modal')._config.keyboard = false;
    } else {
      modalRef.data('bs.modal')._config.keyboard = true;
    }

    modalRef.modal('show');
  }

  getCRMIcon(crmType: string): string {
    const fontAwesomeCRMs = new Set(['hubspot', 'salesforce']);

    if (fontAwesomeCRMs.has(crmType)) {
      return `<i class="fab fa-${crmType}"></i>`;
    }

    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  // --------------- SAVED SEARCHES ---------------
  updateAfterSearchHasBeenSaved(data): void {
    if (data.success) {
      this.pubMethods.showInfoMessage(
        'This search has been saved to your account.'
      );
    }
    this.savedSearchName = '';
    this.makeSavedSearchShared = false;
  }

  saveSearch(): void {
    if (!this.savedSearchName?.trim().length) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Enter a valid name'
      );
      return;
    }
    const requiredOutput = this.getJSONParamsToSave(false);
    this.dashboardService
      .saveUserSearch(
        this.savedSearchName,
        requiredOutput,
        this.makeSavedSearchShared
      )
      .subscribe(data => this.updateAfterSearchHasBeenSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }

  onSavedSearchesCollapse(): void {
    $('#saved-searches-collapse-card').on('show.bs.collapse', () => {
      if (this.privateSavedSearches.length || this.sharedSavedSearches.length)
        return;
      this.loadingSavedSearches.set(true);
      this.accountService.getSavedSearchDetails().subscribe(data => {
        if (this.pubMethods.hasData(data)) {
          this.privateSavedSearches = data.saved_searches?.private;
          this.sharedSavedSearches = data.saved_searches?.shared;
        }
        this.loadingSavedSearches.set(false);
      });
    });
  }

  // --------------- SAVED SEARCHES ---------------

  // --------------- ALERTS ---------------

  chooseToAllowCreateAlertForm(): void {
    const maxCompaniesToCreateAlertFor = 200000;
    if (
      this.isLoading().loadingCompanyCount ||
      this.companyCount().count > maxCompaniesToCreateAlertFor
    ) {
      if (this.isLoading().loadingCompanyCount) {
        this.pubMethods.log(
          'Please wait until the search has loaded to create an alert.'
        );
      } else if (this.companyCount().count > maxCompaniesToCreateAlertFor) {
        this.pubMethods.log(
          'You can only create an alert for a search with fewer than ' +
            maxCompaniesToCreateAlertFor +
            ' initial organisations.'
        );
      }
    } else {
      this.openResultsActionModal('createAlert');
    }
  }

  updateAfterAlertHadBeenSaved(data): void {
    if (data.success) {
      if (this.isSuperUser) {
        const optionalWarning = data.warning_message
          ? data.warning_message
          : '';
        this.pubMethods.showInfoMessage(
          'This user will be alerted on a ' +
            this.selectedAlertFrequency +
            ' basis of new organisations matching these criteria. ' +
            optionalWarning
        );
      } else {
        this.pubMethods.showInfoMessage(
          'You will be alerted on a ' +
            this.selectedAlertFrequency +
            ' basis of new organisations matching these criteria.'
        );
      }
    }
    this.savedAlertName = '';
  }

  createAlert(): void {
    if (!this.savedAlertName?.trim().length) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Enter a valid alert name.'
      );
      return;
    }

    const requiredOutput = this.getJSONParamsToSave(true);
    this.dashboardService
      .saveUserAlert(
        this.savedAlertName,
        this.selectedAlertFrequency,
        this.teamMemberIdToCreateAlertFor,
        requiredOutput
      )
      .subscribe(data => this.updateAfterAlertHadBeenSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }
  // --------------- ALERTS ---------------

  // --------------- PROPENSITY SCORE ---------------

  updatePropensityModelsForUI(): void {
    if (this.propensityModels) {
      this.editablePropensityModels = this.propensityModels.filter(
        model => model.can_edit
      );
    }
  }

  updateZintPropensityScoreDetails(data): void {
    this.propensityModels = data.propensity_models;
    if (this.propensityModels && this.propensityModels.length > 0) {
      this.selectedPropensityModelId = this.propensityModels[0].id;
    }
    this.updatePropensityModelsForUI();
  }

  newPropensityScoreTAMCreated(data): void {
    if (data.success) {
      this.propensityModels = data.propensity_models;
      if (this.propensityModels && this.propensityModels.length > 0) {
        this.selectedPropensityModelId = data.new_propensity_model_id;
      }
      $('#propensityScoreTAMDefinition').modal('hide');
      this.updatePropensityModelsForUI();
      this.pubMethods.showInfoMessage(
        'Your Propensity Model has been created. Scores will begin to update in the next 1-2 hours.'
      );
    }
  }

  checkIfAddingNewPropensityScoreModel(
    possibleStringToTriggerNewPropensityModel
  ): void {
    if (
      possibleStringToTriggerNewPropensityModel ==
      this.stringToTriggerNewPropensityModel
    ) {
      this.selectedPropensityModelId = null;

      $('#searchResultsActionsModal').modal('hide');
      $('#propensityScoreTAMDefinition').modal('show');
    }
  }

  definePropensityScoreTAM(): void {
    const jsonParams = this.getJSONParamsToSave(true);
    this.zintPropensityScoreService
      .savePropensityScoreTAM(this.propensityScoreTAMName, jsonParams)
      .subscribe(data => this.newPropensityScoreTAMCreated(data));
  }

  updatePropensityScoreComponentAfterSaved(data): void {
    if (data.success) {
      this.pubMethods.showInfoMessage(
        'This Propensity Score Component was saved.'
      );
      this.propensityScoreComponentDescription = '';
      this.propensityScoreWeighting = null;
    }
  }

  savePropensityScoreComponent(): void {
    if (
      this.propensityScoreWeighting === null ||
      this.propensityScoreWeighting === undefined
    ) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'enter a valid weighting'
      );
      return;
    }

    const jsonParams = this.getJSONParamsToSave(true);

    this.zintPropensityScoreService
      .savePropensityScoreComponent(
        this.selectedPropensityModelId,
        this.propensityScoreComponentDescription,
        this.propensityScoreWeighting,
        jsonParams
      )
      .subscribe(data => this.updatePropensityScoreComponentAfterSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }

  validatepropensityScoreWeightingValue(): void {
    if (
      this.propensityWeightingRange &&
      this.propensityWeightingRange.length == 2
    ) {
      if (this.propensityScoreWeighting < this.propensityWeightingRange[0]) {
        this.propensityScoreWeighting = this.propensityWeightingRange[0];
      }
      if (this.propensityScoreWeighting > this.propensityWeightingRange[1]) {
        this.propensityScoreWeighting = this.propensityWeightingRange[1];
      }
    }
  }

  updateAfterTAMOverwritten(data): void {
    if (data.success) {
      this.pubMethods.showInfoMessage(
        'Your TAM has been overwritten. Scores will begin to update in the next 1-2 hours.'
      );
      $('#editPropensityModelTAM').modal('hide');
    }
  }

  updatePropensityModelTAM(): void {
    const jsonParams = this.getJSONParamsToSave(true);
    this.zintPropensityScoreService
      .overwritePropensityScoreTAM(
        this.selectedPropensityModelIdToUpdateTAM,
        jsonParams
      )
      .subscribe(data => this.updateAfterTAMOverwritten(data));
  }

  // --------------- PROPENSITY SCORE ---------------

  // --------------- TERRITORY ---------------

  updateTerritoryAssignmentDetails(hasTerritoryAssignmentAccessMessage) {
    this.hasTerritoryAssignmentAccessMessage =
      hasTerritoryAssignmentAccessMessage;
    this.territoryService.listTerritories().subscribe(data => {
      this.availableTerritories = data;
      this.areTerritoriesEnforced = data.some(
        territory => territory['is_enforced']
      );
    });
  }

  searchCompaniesWithTerritoryFilter(territory: Record<string, any> | null) {
    this.selectedTerritory = territory;

    this.searchCompaniesAfterFilters(true);
  }

  saveTerritory(): void {
    if (!this.territoryName?.trim().length) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Enter a valid territory name'
      );
      return;
    }

    const jsonParams = this.getJSONParamsToSave(true);
    this.territoryService
      .saveTerritory(this.territoryName, jsonParams)
      .subscribe(data => this.updateAfterTerritoryHasBeenSaved(data));

    $('#searchResultsActionsModal').modal('hide');
  }

  updateAfterTerritoryHasBeenSaved(data): void {
    if (data.name) {
      this.pubMethods.showInfoMessage(
        `Territory ${data.name} has been saved to your organisation.`
      );
    }
    this.territoryName = '';
  }

  // --------------- TERRITORY ---------------

  // RESETTING
  resetSpecificFilter(filter: string): void {
    const checkboxEl = $(`input[name='${filter}']:checkbox`);

    const inputEl = $(`input[name='${filter}']`);
    checkboxEl.prop('checked', false);
    inputEl.val('');

    this.resetAllZintSliders();
    this.resetConfigTextInputs(filter);
    this.resetMultiCheckboxOptions(filter);
  }

  resetAllFilters(): void {
    this.resetInputsThroughJquery();
    this.resetConfigTextInputs();
    this.resetMultiCheckboxOptions();
    this.resetAllZintSliders();
    this.summarySemanticSearchMinScore = this.defaultSemanticMatchScore;
    this.sortKey = '';
    this.sortDirectionIsAscending = false;
    this.selectedAddressTypeOption = '';
    this.selectedLatestAccountsGroupOption = '';
    this.selectedLatestAccountsAuditOption = '';
  }

  resetAllFiltersAndUpdateFilters(): void {
    this.resetAllFilters();
    this.updateFilters();
    const params = this.allFilterParams();
    this.searchTags = JSON.parse(JSON.stringify(params)).updates;
    this.activeParamsFromMapper = [];
  }

  resetConfigTextInputs(specificOptionKey?: string): void {
    if (specificOptionKey) {
      let foundKey: string | undefined;
      let foundIndex: number = -1;

      for (const [key, value] of Object.entries(this.zintTextInputConfigs)) {
        foundIndex = value.findIndex(
          (obj: any) => obj.label === specificOptionKey
        );
        if (foundIndex !== -1) {
          foundKey = key;
          break;
        }
      }
      if (foundKey && foundIndex !== -1) {
        this.zintTextInputConfigs[foundKey][foundIndex]['value'] = '';
      }
      return;
    }

    // reset value for all
    Object.values(this.zintTextInputConfigs).forEach(array => {
      array.forEach((obj: any) => {
        obj.value = '';
      });
    });
    this.configTextInputValues = {};
  }

  resetMultiCheckboxOptions(specificOptionKey?: string) {
    const optionsDict = this.getAllOptionsDict();

    if (specificOptionKey) {
      const options = optionsDict[specificOptionKey]?.options;
      options?.forEach(option => {
        option['checked'] = false;
      });
      return;
    }

    for (const key in optionsDict) {
      // Check 'attribute' property of the option - default to 'checked'
      const attribute = optionsDict[key]?.attribute || 'checked';

      const options = optionsDict[key].options;
      options.forEach(option => {
        option[attribute] = false;
      });
    }
  }

  resetInputsThroughJquery(): void {
    $('#search-area').find('input:checkbox').prop('checked', false);
    $('#search-area').find('input:text').not('.slider-input').val('');
    $('#search-area').find('select').val('');
  }

  resetAllZintSliders(): void {
    const doubleSliderElements = [
      this.numberUBOsSliderElement,
      this.companyAgeSliderElement,
      this.accountsDueSliderElement,
      this.latestAccountsSubmittedSliderElement,
      this.investmentRaisedSliderElement,
      this.shareFilingEventWithinDaysSliderElement,
      this.shareFilingCapitalValueSliderElement,
      this.grantAmountSliderElement,
      this.grantMonthsUntilProjectEndSliderElement,
      this.headcountSliderElement,
      this.headcountGrowthSliderElement,
      this.departmentHeadcountSliderElement,
      this.averageDirectorAgeSliderElement,
      this.numberOfDirectorsSliderElement,
      this.numberOfPSCsSliderElement,
      this.recentDirectorshipMonthRangeSliderElement,
      this.websiteVisitorsSliderElement,
      this.eventsDayRangeSliderElement,
      this.numberOfRolesHiringForSliderElement,
      this.daysUntilFinancialYearEndSliderElement,
      this.daysUntilAnniversarySliderElement,
      this.alertsDayRangeSliderElement,
      this.numberOfKnownOfficeLocationsSliderElement,
      this.geographicalRevenueSliderElement,
      this.totalEnergyKwhSliderElement,
      this.gasKwhSliderElement,
      this.electricKwhSliderElement,
      this.scope1KwhSliderElement,
      this.scope2KwhSliderElement,
      this.scope3KwhSliderElement,
      this.totalEmissionsKgCo2eSliderElement,
      this.scope1KgCo2eSliderElement,
      this.scope2KgCo2eSliderElement,
      this.scope3KgCo2eSliderElement,
      this.kgCo2ePerMillionRevenueSliderElement,
      this.kgCo2ePerEmployeeSliderElement,
      this.patentFiledWithinDaysSliderElement,
      this.patentAwardedWithinDaysSliderElement,
      this.contractValueSliderElement,
      this.contractStartMonthsSliderElement,
      this.contractEndMonthsSliderElement,
      this.chargeCreatedSliderElement,
    ];

    const singleSliderElements = [
      this.mileRadiusSliderElement,
      this.importExportSliderElement,
      this.importExportFrequencySliderElement,
      this.importExportFrequencyMonthCountSliderElement,
      this.numberOfRolesHiringForDayLimitSliderElement,
      this.hiringDayLimitSliderElement,
      this.newsDayRangeSliderElement,
      this.accountsSearchDayRangeSliderElement,
      this.eventsMileRadiusSliderElement,
      this.contractAwardedWithinMonthsSliderElement,
    ];

    const allManualSliders = [...doubleSliderElements, ...singleSliderElements];

    allManualSliders.forEach(slider => {
      if (slider) {
        slider.reset();
      }
    });

    this.allConfigSlidersChildren?.forEach(slider => {
      if (slider) {
        slider.reset();
      }
    });

    // this is to clear searchTags for configSliders
    this.sliderValues = {};
    this.confidenceValues = {};

    this.setZintSliderLimitsToNull();
  }

  setZintSliderLimitsToNull(): void {
    this.employeeLowerCount = null;
    this.employeeUpperCount = null;
    this.departmentHeadCountLowerCount = null;
    this.departmentHeadCountUpperCount = null;
    this.lowerHeadCountGrowthPercentage = null;
    this.upperHeadCountGrowthPercentage = null;
    this.lowerAge = null;
    this.upperAge = null;
    this.mileRadiusOfPostcode = null;
    this.lowerNumberOfKnownOfficeLocations = null;
    this.upperNumberOfKnownOfficeLocations = null;
    this.lowerGeographicalRevenueSelected = null;
    this.upperGeographicalRevenueSelected = null;
    this.importExportTimeScale = null;
    this.webTrafficLow = null;
    this.webTrafficHigh = null;
    this.lowerAverageDirectorAgeRange = null;
    this.upperAverageDirectorAgeRange = null;
    this.lowerNumberOfDirectors = null;
    this.upperNumberOfDirectors = null;
    this.lowerNumberOfPSCs = null;
    this.upperNumberOfPSCs = null;
    this.lowerRecentDirectorshipMonth = null;
    this.upperRecentDirectorshipMonth = null;
    this.lowerDaysUntilFinancialYearEnd = null;
    this.upperDaysUntilFinancialYearEnd = null;
    this.lowerDaysUntilAnniversary = null;
    this.upperDaysUntilAnniversary = null;
    this.selectedHiringDayLimit = null;
    this.lowerNumberOfRolesHiringFor = null;
    this.upperNumberOfRolesHiringFor = null;
    this.selectedNumberOfRolesHiringForDayLimit = null;
    this.lowerInvestmentAmount = null;
    this.upperInvestmentAmount = null;
    this.shareFilingEventWithinDays = null;
    this.lowerShareFilingCapitalValue = null;
    this.upperShareFilingCapitalValue = null;
    this.lowerAccountsDueSelected = null;
    this.upperAccountsDueSelected = null;
    this.lowerLatestAccountsSubmittedSelected = null;
    this.upperLatestAccountsSubmittedSelected = null;
    this.lowerGrantAmount = null;
    this.upperGrantAmount = null;
    this.grantLowerMonthsUntilProjectEnd = null;
    this.grantUpperMonthsUntilProjectEnd = null;
    this.mileRadiusOfEventsSelected = null;
    this.eventsWithinDaysLower = null;
    this.eventsWithinDaysUpper = null;
    this.newsWithinDays = null;
    this.accountsSearchWithinDays = null;
    this.lowerTotalEnergyKwhSelected = null;
    this.upperTotalEnergyKwhSelected = null;
    this.lowerGasKwhSelected = null;
    this.upperGasKwhSelected = null;
    this.lowerElectricKwhSelected = null;
    this.upperElectricKwhSelected = null;
    this.lowerScope1KwhSelected = null;
    this.upperScope1KwhSelected = null;
    this.lowerScope2KwhSelected = null;
    this.upperScope2KwhSelected = null;
    this.lowerScope3KwhSelected = null;
    this.upperScope3KwhSelected = null;
    this.lowerTotalEmissionsKgCo2eSelected = null;
    this.upperTotalEmissionsKgCo2eSelected = null;
    this.lowerScope1KgCo2eSelected = null;
    this.upperScope1KgCo2eSelected = null;
    this.lowerScope2KgCo2eSelected = null;
    this.upperScope2KgCo2eSelected = null;
    this.lowerScope3KgCo2eSelected = null;
    this.upperScope3KgCo2eSelected = null;
    this.lowerKgCo2ePerMillionRevenueSelected = null;
    this.upperKgCo2ePerMillionRevenueSelected = null;
    this.lowerKgCo2ePerEmployeeSelected = null;
    this.upperKgCo2ePerEmployeeSelected = null;
    this.patentFiledWithinDays = null;
    this.lowerPatentAwardedDays = null;
    this.upperPatentAwardedDays = null;
    this.lowerContractValueSelected = null;
    this.upperContractValueSelected = null;
    this.contractAwardedWithinMonths = null;
    this.lowerContractStartMonthsSelected = null;
    this.upperContractStartMonthsSelected = null;
    this.lowerContractEndMonthsSelected = null;
    this.upperContractEndMonthsSelected = null;
    this.lowerAlertsDayRange = null;
    this.upperAlertsDayRange = null;
  }

  // END RESETTING

  updateEmployeeRange(sliderEvent: Event): void {
    this.employeeLowerCount = sliderEvent['lower'];
    this.employeeUpperCount = sliderEvent['upper'];
  }

  updateHeadcountGrowthRange(sliderEvent: Event): void {
    this.lowerHeadCountGrowthPercentage = sliderEvent['lower'];
    this.upperHeadCountGrowthPercentage = sliderEvent['upper'];
  }

  updateDepartmentHeadcountRange(sliderEvent: Event): void {
    this.departmentHeadCountLowerCount = sliderEvent['lower'];
    this.departmentHeadCountUpperCount = sliderEvent['upper'];
  }

  updateNumberUBOsRange(sliderEvent: Event): void {
    this.lowerNumberOfUBOs = sliderEvent['lower'];
    this.upperNumberOfUBOs = sliderEvent['upper'];
  }

  updateCompanyAgeRange(sliderEvent: Event): void {
    this.lowerAge = sliderEvent['lower'];
    this.upperAge = sliderEvent['upper'];
  }

  updatePostcodeMileageRange(sliderEvent: Event): void {
    this.mileRadiusOfPostcode = sliderEvent['upper'];
  }

  updateNumberOfKnownOfficeLocationsRange(sliderEvent: Event): void {
    this.lowerNumberOfKnownOfficeLocations = sliderEvent['lower'];
    this.upperNumberOfKnownOfficeLocations = sliderEvent['upper'];
  }

  updateGeographicalRevenueRangeSelection(sliderEvent: Event): void {
    this.lowerGeographicalRevenueSelected = sliderEvent['lower'];
    this.upperGeographicalRevenueSelected = sliderEvent['upper'];
  }

  updateImportExportTimeScale(sliderEvent: Event): void {
    this.updateToDefaultImportingOrExporting();
    this.importExportTimeScale = sliderEvent['upper'];
  }

  updateImportExportFrequency(sliderEvent: Event): void {
    this.updateImportExportFrequencyToDefaultImportingOrExporting();
    this.importExportFrequency = sliderEvent['upper'];
  }

  updateImportExportFrequencyMonthCount(sliderEvent: Event): void {
    this.updateImportExportFrequencyToDefaultImportingOrExporting();
    this.importExportFrequencyMonthCount = sliderEvent['upper'];
  }

  updateWebTraffic(sliderEvent: Event): void {
    this.webTrafficLow = sliderEvent['lower'];
    this.webTrafficHigh = sliderEvent['upper'];
  }

  updateAverageDirectorAgeRange(sliderEvent: Event): void {
    this.lowerAverageDirectorAgeRange = sliderEvent['lower'];
    this.upperAverageDirectorAgeRange = sliderEvent['upper'];
  }

  updateNumberOfDirectorsRange(sliderEvent: Event): void {
    this.lowerNumberOfDirectors = sliderEvent['lower'];
    this.upperNumberOfDirectors = sliderEvent['upper'];
  }

  updateNumberOfPSCsRange(sliderEvent: Event): void {
    this.lowerNumberOfPSCs = sliderEvent['lower'];
    this.upperNumberOfPSCs = sliderEvent['upper'];
  }

  updateRecentDirectorshipMonthRange(sliderEvent: Event): void {
    this.lowerRecentDirectorshipMonth = sliderEvent['lower'];
    this.upperRecentDirectorshipMonth = sliderEvent['upper'];
  }

  updateFinancialYearEndRange(sliderEvent: Event): void {
    this.lowerDaysUntilFinancialYearEnd = sliderEvent['lower'];
    this.upperDaysUntilFinancialYearEnd = sliderEvent['upper'];
  }

  updateAnniversaryRange(sliderEvent: Event): void {
    this.lowerDaysUntilAnniversary = sliderEvent['lower'];
    this.upperDaysUntilAnniversary = sliderEvent['upper'];
  }

  updateHiringDayRange(sliderEvent: Event): void {
    this.selectedHiringDayLimit = sliderEvent['upper'];
  }

  updateNumberOfRolesHiringForRange(sliderEvent: Event): void {
    this.lowerNumberOfRolesHiringFor = sliderEvent['lower'];
    this.upperNumberOfRolesHiringFor = sliderEvent['upper'];
  }

  updateNumberOfRolesHiringForDayRange(sliderEvent: Event): void {
    this.selectedNumberOfRolesHiringForDayLimit = sliderEvent['upper'];
  }

  updateInvestmentRaisedRanges(sliderEvent: Event): void {
    this.lowerInvestmentAmount = sliderEvent['lower'];
    this.upperInvestmentAmount = sliderEvent['upper'];
  }

  updateShareFilingEventWithinDays(sliderEvent: Event): void {
    this.shareFilingEventWithinDays = sliderEvent['upper'];
  }

  updateShareFilingCapitalValue(sliderEvent: Event): void {
    this.lowerShareFilingCapitalValue = sliderEvent['lower'];
    this.upperShareFilingCapitalValue = sliderEvent['upper'];
  }

  updateAccountsDueInRange(sliderEvent: Event): void {
    this.lowerAccountsDueSelected = sliderEvent['lower'];
    this.upperAccountsDueSelected = sliderEvent['upper'];
  }

  updatelatestAccountsSubmittedInRange(sliderEvent: Event): void {
    this.lowerLatestAccountsSubmittedSelected = sliderEvent['lower'];
    this.upperLatestAccountsSubmittedSelected = sliderEvent['upper'];
  }

  updateGrantRangeSelected(sliderEvent: Event): void {
    this.lowerGrantAmount = sliderEvent['lower'];
    this.upperGrantAmount = sliderEvent['upper'];
  }

  updateGrantMonthsUntilProjectEndSelected(sliderEvent: Event): void {
    this.grantLowerMonthsUntilProjectEnd = sliderEvent['lower'];
    this.grantUpperMonthsUntilProjectEnd = sliderEvent['upper'];
  }

  updateEventsMileageRange(sliderEvent: Event): void {
    this.mileRadiusOfEventsSelected = sliderEvent['upper'];
  }

  updateEventsDaysRange(sliderEvent: Event): void {
    this.eventsWithinDaysLower = sliderEvent['lower'];
    this.eventsWithinDaysUpper = sliderEvent['upper'];
  }

  updateNewsDays(sliderEvent: Event): void {
    this.newsWithinDays = sliderEvent['upper'];
  }

  updateAccountsSearchDayRange(sliderEvent: Event): void {
    this.accountsSearchWithinDays = sliderEvent['upper'];
  }

  updateAlertsDayRange(sliderEvent: Event): void {
    this.lowerAlertsDayRange = sliderEvent['lower'];
    this.upperAlertsDayRange = sliderEvent['upper'];
  }

  updateTotalEnergyKwhRangeSelection(sliderEvent: Event): void {
    this.lowerTotalEnergyKwhSelected = sliderEvent['lower'];
    this.upperTotalEnergyKwhSelected = sliderEvent['upper'];
  }

  updateGasKwhRangeSelection(sliderEvent: Event): void {
    this.lowerGasKwhSelected = sliderEvent['lower'];
    this.upperGasKwhSelected = sliderEvent['upper'];
  }

  updateElectricKwhRangeSelection(sliderEvent: Event): void {
    this.lowerElectricKwhSelected = sliderEvent['lower'];
    this.upperElectricKwhSelected = sliderEvent['upper'];
  }

  updateScope1KwhRangeSelection(sliderEvent: Event): void {
    this.lowerScope1KwhSelected = sliderEvent['lower'];
    this.upperScope1KwhSelected = sliderEvent['upper'];
  }

  updateScope2KwhRangeSelection(sliderEvent: Event): void {
    this.lowerScope2KwhSelected = sliderEvent['lower'];
    this.upperScope2KwhSelected = sliderEvent['upper'];
  }

  updateScope3KwhRangeSelection(sliderEvent: Event): void {
    this.lowerScope3KwhSelected = sliderEvent['lower'];
    this.upperScope3KwhSelected = sliderEvent['upper'];
  }

  updateTotalEmissionsKgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerTotalEmissionsKgCo2eSelected = sliderEvent['lower'];
    this.upperTotalEmissionsKgCo2eSelected = sliderEvent['upper'];
  }

  updateScope1KgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerScope1KgCo2eSelected = sliderEvent['lower'];
    this.upperScope1KgCo2eSelected = sliderEvent['upper'];
  }

  updateScope2KgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerScope2KgCo2eSelected = sliderEvent['lower'];
    this.upperScope2KgCo2eSelected = sliderEvent['upper'];
  }

  updateScope3KgCo2eRangeSelection(sliderEvent: Event): void {
    this.lowerScope3KgCo2eSelected = sliderEvent['lower'];
    this.upperScope3KgCo2eSelected = sliderEvent['upper'];
  }

  updateKgCo2ePerMillionRevenueRangeSelection(sliderEvent: Event): void {
    this.lowerKgCo2ePerMillionRevenueSelected = sliderEvent['lower'];
    this.upperKgCo2ePerMillionRevenueSelected = sliderEvent['upper'];
  }

  updateKgCo2ePerEmployeeRangeSelection(sliderEvent: Event): void {
    this.lowerKgCo2ePerEmployeeSelected = sliderEvent['lower'];
    this.upperKgCo2ePerEmployeeSelected = sliderEvent['upper'];
  }

  updatePatentFiledWithinDays(sliderEvent: Event): void {
    this.patentFiledWithinDays = sliderEvent['upper'];
  }

  updatePatentAwardedWithinDays(sliderEvent: Event): void {
    this.lowerPatentAwardedDays = sliderEvent['lower'];
    this.upperPatentAwardedDays = sliderEvent['upper'];
  }

  updateContractAwardedWithinMonths(sliderEvent: Event): void {
    this.contractAwardedWithinMonths = sliderEvent['upper'];
  }

  updateContractValueRangeSelection(sliderEvent: Event): void {
    this.lowerContractValueSelected = sliderEvent['lower'];
    this.upperContractValueSelected = sliderEvent['upper'];
  }

  updateContractStartMonthsRangeSelection(sliderEvent: Event): void {
    this.lowerContractStartMonthsSelected = sliderEvent['lower'];
    this.upperContractStartMonthsSelected = sliderEvent['upper'];
  }

  updateContractEndMonthsRangeSelection(sliderEvent: Event): void {
    this.lowerContractEndMonthsSelected = sliderEvent['lower'];
    this.upperContractEndMonthsSelected = sliderEvent['upper'];
  }

  updateChargeCreatedRangeSelection(sliderEvent: Event): void {
    this.chargeCreatedMonthsAgoLower = sliderEvent['lower'];
    this.chargeCreatedMonthsAgoUpper = sliderEvent['upper'];
  }

  triggerConfigSliderUpdate(
    sliderUpdatedValues: Record<string, any>,
    key: string
  ): void {
    const { lower, upper } = sliderUpdatedValues;
    if (lower === undefined && upper === undefined) {
      delete this.sliderValues[key];
    } else {
      this.sliderValues[key] = { lower, upper };
    }
  }

  triggerConfigSliderWithObjectUpdate(
    sliderUpdatedValues: Record<string, any>,
    objectWithDetails: Record<string, any>,
    objectIdentifier: string = 'airaQuestionId'
  ): void {
    const { lower, upper } = sliderUpdatedValues;
    if (lower === undefined && upper === undefined) {
      delete this.sliderValues[objectWithDetails.label];
    } else {
      const confidence = this.confidenceValues[`confidence:${objectWithDetails.label}Aira${objectWithDetails.airaQuestionId}`];
      const confLower = confidence?.lower ?? null;
      const confUpper = confidence?.upper ?? null;
      const lowerDetails =
        lower === null || lower === undefined
          ? null
          : {
              [objectIdentifier]: objectWithDetails[objectIdentifier],
              value: lower,
              fieldName: objectWithDetails['fieldName'],
              filterType: objectWithDetails['filterType'],
              linkedFieldName: objectWithDetails['linkedFieldName'],
              lowerConfidence: confLower,
              upperConfidence: confUpper,
            };
      const upperDetails =
        upper === null || upper === undefined
          ? null
          : {
              [objectIdentifier]: objectWithDetails[objectIdentifier],
              value: upper,
              fieldName: objectWithDetails['fieldName'],
              filterType: objectWithDetails['filterType'],
              linkedFieldName: objectWithDetails['linkedFieldName'],
              lowerConfidence: confLower,
              upperConfidence: confUpper,
            };
      this.sliderValues[objectWithDetails.label] = {
        lower: lowerDetails,
        upper: upperDetails,
      };
    }
  }

  triggerConfigTextInputWithObjectUpdate(
    textValue: string,
    objectWithDetails: Record<string, any>,
    objectIdentifier: string = 'airaQuestionId'
  ): void {
    if (textValue === null || textValue === undefined || textValue === '') {
      delete this.configTextInputValues[objectWithDetails.label];
    } else {
      const confidence = this.confidenceValues[`confidence:${objectWithDetails.label}Aira${objectWithDetails.airaQuestionId}`];
      const confLower = confidence?.lower ?? null;
      const confUpper = confidence?.upper ?? null;
      this.configTextInputValues[objectWithDetails.label] = {
        [objectIdentifier]: objectWithDetails[objectIdentifier],
        value: textValue,
        fieldName: objectWithDetails['fieldName'],
        filterType: objectWithDetails['filterType'],
        linkedFieldName: objectWithDetails['linkedFieldName'],
        lowerConfidence: confLower,
        upperConfidence: confUpper,
      };
    }
  }

  filterMagicCountriesList(
    searchTerm: string,
    sectionKey: any,
    groupKey: any
  ): void {
    const group = this.linkedAiraFilters[sectionKey][groupKey];
    const multiSelect = group.find(
      filter => filter.filterType === 'multi_select'
    );

    if (!multiSelect) {
      return;
    }

    const shouldShowAll = searchTerm.trim().length < 2;
    const lowercasedTerm = searchTerm.toLowerCase();

    multiSelect.options = multiSelect.options.map(option => {
      const shouldShow =
        shouldShowAll || option.name.toLowerCase().includes(lowercasedTerm);
      return { ...option, shouldShow };
    });
  }

  triggerConfigMultiSelectInputWithObjectUpdate(
    objectWithDetails: Record<string, any>,
    objectIdentifier: string = 'airaQuestionId'
  ): void {
    const checkedValues = objectWithDetails.options
      .filter(opt => opt.checked)
      .map(filtered => filtered.value);

    if (!checkedValues?.length) {
      delete this.configMultiSelectValues[objectWithDetails.label];
    } else {
      const confidence = this.confidenceValues[`confidence:${objectWithDetails.label}Aira${objectWithDetails.airaQuestionId}`];
      const confLower = confidence?.lower ?? null;
      const confUpper = confidence?.upper ?? null;
      this.configMultiSelectValues[objectWithDetails.label] = {
        [objectIdentifier]: objectWithDetails[objectIdentifier],
        value: checkedValues,
        fieldName: objectWithDetails['fieldName'],
        filterType: objectWithDetails['filterType'],
        linkedFieldName: objectWithDetails['linkedFieldName'],
        lowerConfidence: confLower,
        upperConfidence: confUpper,
      };
    }
  }

  // Confidence slider: track per-filter confidence bounds and merge into existing values
  triggerConfidenceSliderUpdate = (
    sliderUpdatedValues: { lower?: number; upper?: number },
    objectWithDetails: Record<string, any>
  ): void => {
    const lower = sliderUpdatedValues?.lower ?? null;
    const upper = sliderUpdatedValues?.upper ?? null;


    const label = objectWithDetails?.label;
    const airaId = objectWithDetails?.airaQuestionId;
    if (!label) return;

    if (airaId) {
      const confKey = `confidence:${label}Aira${airaId}`;
      this.confidenceValues[confKey] = { lower, upper };
    }

    // Update existing slider entries if present
    const existingSliderEntry = this.sliderValues[label];
    if (existingSliderEntry) {
      if (existingSliderEntry.lower) {
        existingSliderEntry.lower = {
          ...existingSliderEntry.lower,
          lowerConfidence: lower,
          upperConfidence: upper,
        };
      }
      if (existingSliderEntry.upper) {
        existingSliderEntry.upper = {
          ...existingSliderEntry.upper,
          lowerConfidence: lower,
          upperConfidence: upper,
        };
      }
    }

    // Update existing text input entry if present
    if (this.configTextInputValues[label]) {
      this.configTextInputValues[label] = {
        ...this.configTextInputValues[label],
        lowerConfidence: lower,
        upperConfidence: upper,
      };
    }

    // Update existing multi-select entry if present
    if (this.configMultiSelectValues[label]) {
      this.configMultiSelectValues[label] = {
        ...this.configMultiSelectValues[label],
        lowerConfidence: lower,
        upperConfidence: upper,
      };
    }
  };

  getConfidenceKey(filter: Record<string, any>): string {
    if (!filter) return '';
    return `confidence:${filter.label}Aira${filter.airaQuestionId}`;
  }

  toggleConfidencePanel(filter: Record<string, any>): void {
    const key = this.getConfidenceKey(filter);
    this.confidenceOpen[key] = !this.confidenceOpen[key];
  }

  getChildSics(sicObject): Array<any> {
    const childrenSicIds = sicObject['children'];
    return this.companySICCodeOptions.filter(
      sic => childrenSicIds.indexOf(sic.id) > -1
    );
  }

  collapseSICCodes(sicRequired): void {
    const childSics = this.getChildSics(sicRequired);
    for (const sic of childSics) {
      sic.shouldShow = false;
      this.collapseSICCodes(sic);
    }
  }

  expandOrCollapseSICCodes(sicID): void {
    const sicRequired = this.companySICCodeOptions.filter(
      sic => sic.id == sicID
    )[0];

    const childSics = this.getChildSics(sicRequired);

    if (childSics.some(e => e.shouldShow == true)) {
      this.collapseSICCodes(sicRequired);
    } else {
      childSics.map(function (e) {
        e.shouldShow = true;
      });
    }
  }

  updateVisibleImportExportCodesAfterSearch(data): void {
    this.importExportCategories = data.import_export_results;
  }

  searchImportExportCodes(): void {
    this.dashboardService
      .searchImportExportCodes(this.importExportTextSearch)
      .subscribe(data => this.updateVisibleImportExportCodesAfterSearch(data));
  }

  resetImportExportSearch(): void {
    this.dashboardService
      .getInitialImportExportCategories()
      .subscribe(
        data => (this.importExportCategories = data.import_export_sections)
      );
    this.importExportTextSearch = '';
  }

  updateImpExpCats(data, id, impExpType): void {
    for (let i = 0; i < this.importExportCategories.length; i++) {
      // tslint:disable-next-line:prefer-const
      var existingCat = this.importExportCategories[i];
      if (existingCat.id == id && existingCat.impExpType == impExpType) {
        // tslint:disable-next-line:prefer-const
        var insertionIndex = i;
        existingCat.isExpanded = true;
        break;
      }
    }

    for (const child of data.reverse()) {
      existingCat.children.push(String(child.id + '_' + child.impExpType));
      this.importExportCategories.splice(insertionIndex + 1, 0, child);
    }
  }

  collapseImpExpCat(importExportCategory): void {
    for (let i = 0; i < importExportCategory.children.length; i++) {
      const alreadyOpenChild = importExportCategory.children[i];
      const components = alreadyOpenChild.split('_');
      const _id = components[0];
      const impExpType = components[1];
      for (let j = 0; j < this.importExportCategories.length; j++) {
        const existingCat = this.importExportCategories[j];
        if (existingCat.id == _id && existingCat.impExpType == impExpType) {
          this.collapseImpExpCat(existingCat);
          this.importExportCategories.splice(j, 1);
          break;
        }
      }
    }
    importExportCategory.children = [];
    importExportCategory.isExpanded = false;
  }

  expandOrCollapseImportExportCategories(importExportCategory): void {
    if (importExportCategory.isExpanded) {
      this.collapseImpExpCat(importExportCategory);
    } else {
      this.dashboardService
        .getChildImportExportCategories(
          importExportCategory.id,
          importExportCategory.impExpType,
          importExportCategory.level
        )
        .subscribe(data =>
          this.updateImpExpCats(
            data,
            importExportCategory.id,
            importExportCategory.impExpType
          )
        );
    }
  }

  expandOrCollapseItems(companyChildren, type): void {
    const childStatusOptions = this[type].filter(
      el => companyChildren.indexOf(el.name) > -1
    );
    var showValue = childStatusOptions.some(e => e.shouldShow == true)
      ? false
      : true;
    for (const child of childStatusOptions) {
      child.shouldShow = showValue;
    }
  }

  updateCountOfSelectedCompanies(): void {
    const selectedCompanies = this.companies.filter(
      company => company.checked == true
    );
    this.totalCompaniesSelected = selectedCompanies.length;
    this.dataStore.changeDataSourceCurrentCompanyIds(
      selectedCompanies.map(company => company.id)
    );
    // Need to do this as current search params need to be shared on individual company downloads so we can get ES column information.request.POST
    this.dataStore.updateJustCurrentSearchParams(this.allFilterParams());
  }

  showOrHideInfoOnAllSelectedCompanies(): void {
    if (this.checkboxSelectAllCompanies) {
      this.showSelectedNumbersSection = true;
      this.companies.map(company => (company.checked = true));
      this.totalCompaniesSelected = this.companies.length;
      this.dataStore.changeDataSourceCurrentCompanyIds(
        this.companies.map(company => company.id)
      );
    } else {
      this.hasSelectedTotalNumberMatchingFilter = false;
      this.showSelectedNumbersSection = false;
      this.companies.map(company => (company.checked = false));
      this.totalCompaniesSelected = null;
    }
  }

  hasSelectedAllCompaniesByQuery(): void {
    this.checkboxSelectAllCompanies = true;
    this.hasSelectedTotalNumberMatchingFilter = true;
    this.showOrHideInfoOnAllSelectedCompanies();
    this.dataStore.changeDataSourceCurrentSearchParams(this.allFilterParams());
  }

  updateAddressTypeOnInputChange(): void {
    if (
      this.postcodeWithinRadius ||
      this.postcodeBeginsWith ||
      this.selectedTownId ||
      this.postcodeDistrict ||
      this.ukRegions?.some(r => r.checked)
    ) {
      if (!this.selectedAddressTypeOption) {
        this.selectedAddressTypeOption = this.addressOptions[0];
      }
    }
  }

  postcodeBeginsWithChanged(): void {
    this.selectedTownId = '';
    this.postcodeDistrict = '';
    this.townName = '';
    this.postcodeWithinRadius = '';
    this.updateAddressTypeOnInputChange();
  }

  postcodeDistrictChanged(): void {
    this.selectedTownId = '';
    this.townName = '';
    this.postcodeWithinRadius = '';
    this.updateAddressTypeOnInputChange();
  }

  ukRegionChanged(): void {
    this.updateAddressTypeOnInputChange();
  }

  postcodeWithinRadiusChanged(): void {
    this.selectedTownId = '';
    this.postcodeDistrict = '';
    this.townName = '';
    this.postcodeBeginsWith = '';
    this.updateAddressTypeOnInputChange();
  }

  updateMatchingTowns(data): void {
    if (data.matching_towns) {
      this.possibleTownNames = data.matching_towns;
    }
  }

  townNameChanged(): void {
    if (this.townName && this.townName.length > 2) {
      this.dashboardService
        .getMatchingTowns(this.townName)
        .subscribe(data => this.updateMatchingTowns(data));
    }
  }

  selectShortlistedTownName(inputID, townName): void {
    this.postcodeWithinRadius = '';
    this.postcodeBeginsWith = '';
    this.selectedTownId = inputID;
    this.townName = townName;
    this.updateAddressTypeOnInputChange();
  }

  // ----------- SUPPLIER SEARCH -----------

  private supplierSearchDebounce: any;

  supplierSearchChanged(supplierType: string): void {
    clearTimeout(this.supplierSearchDebounce);

    const inputProp = supplierType + 'SearchInput';
    if (!this[inputProp] || this[inputProp].length < 3 || this.loadingSupplierType) return;

    this.supplierSearchDebounce = setTimeout(() => {
      this.loadingSupplierType = supplierType;
      this.dashboardService
        .searchSuppliers(this[inputProp], supplierType)
        .subscribe(data => {
          const possibleProp = 'possible' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's';
          this[possibleProp] = data.suppliers || [];
          this.loadingSupplierType = '';
        });
    }, 400);
  }

  addSupplier(supplier: {id: number, canonical_name: string}, supplierType: string): void {
    const selectedProp = 'selected' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's';
    if (!this[selectedProp]) this[selectedProp] = [];
    if (!this[selectedProp].some(s => s.id === supplier.id)) {
      this[selectedProp].push(supplier);
    }
    this[supplierType + 'SearchInput'] = '';
    this['possible' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's'] = [];
  }

  removeSupplier(supplierId: number, supplierType: string): void {
    const selectedProp = 'selected' + supplierType.charAt(0).toUpperCase() + supplierType.slice(1) + 's';
    this[selectedProp] = this[selectedProp].filter(s => s.id !== supplierId);
  }

  // ----------- HEADCOUNTS/DEPARTMENTS -----------

  resetFunctionalDepartmentSearch(): void {
    this.hasSearchedFunctionalDepartment = false;
    this.selectedFunctionalDepartments = this.functionalDepartmentOptions.map(
      option => (option.shouldShow = true)
    );
    this.functionalDepartmentTextSearch = '';
  }

  searchFunctionalDepartment(): void {
    if (
      this.functionalDepartmentTextSearch &&
      this.functionalDepartmentTextSearch.length > 2
    ) {
      this.isSearchingFunctionalDepartments = true;
      this.hasSearchedFunctionalDepartment = true;
      this.functionalDepartmentOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));

      this.functionalDepartmentOptions
        .filter(
          option =>
            option.name
              .toLowerCase()
              .indexOf(this.functionalDepartmentTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));

      this.isSearchingFunctionalDepartments = false;
    }
  }

  checkIfVisible(option) {
    if (option.shouldShow) {
      option.checked = true;
    }
  }

  checkUncheckAllVisibleFunctionalDepartments(): void {
    if (this.selectAllVisibleFunctionalDepartments) {
      this.functionalDepartmentOptions.forEach(option =>
        this.checkIfVisible(option)
      );
    } else {
      this.functionalDepartmentOptions.forEach(
        option => (option.checked = false)
      );
    }
  }

  // ----------- HEADCOUNTS/DEPARTMENTS -----------

  // ----------- KEYWORDS -----------

  removeWebsiteKeyword(keyword): void {
    const index = this.websiteKeywords.indexOf(keyword);
    if (index >= 0) {
      this.websiteKeywords.splice(index, 1);
      this.possibleWebsiteKeywords = [];
    }
  }

  addWebsiteKeyword(keyword): void {
    const index = this.websiteKeywords.indexOf(keyword);
    if (index < 0) {
      this.websiteKeywords.push(keyword);
      this.possibleWebsiteKeywords = [];
      this.websiteKeywordInput = '';
    }
  }

  forceAddWebsiteKeyword(event): void {
    if (this.websiteKeywordInput && this.websiteKeywordInput.length > 2) {
      this.addWebsiteKeyword(this.websiteKeywordInput.toLowerCase());
    }
  }

  updateWebsiteKeywords(data): void {
    if (data.keywords) {
      this.possibleWebsiteKeywords = data.keywords;
    }
  }

  websiteKeywordChanged(): void {
    if (this.websiteKeywordInput && this.websiteKeywordInput.length > 2) {
      this.dashboardService
        .getMatchingWebsiteKeywords(this.websiteKeywordInput)
        .subscribe(data => this.updateWebsiteKeywords(data));
    }
  }

  // ----------- KEYWORDS -----------

  // ----------- EVENTS -----------

  removeMatchingEvent(event): void {
    const index = this.selectedEvents.indexOf(event);
    if (index >= 0) {
      this.selectedEvents.splice(index, 1);
    }
  }

  addMatchingEvent(possibleMatchingEvent): void {
    const alreadyHaveEventCounter = this.selectedEvents.filter(
      event => event.id == possibleMatchingEvent.id
    ).length;
    if (alreadyHaveEventCounter == 0) {
      this.selectedEvents.push(possibleMatchingEvent);
      this.possibleMatchingEvents = [];
      this.eventNameSearchInput = '';
      this.postcodeEventsWithinRadius = '';
    }
  }

  eventNameSearchChanged(): void {
    if (this.eventNameSearchInput && this.eventNameSearchInput.length > 2) {
      this.dashboardService
        .getMatchingEvents(this.eventNameSearchInput)
        .subscribe(data => this.updatePossibleMatchingEvents(data));
    }
  }

  updatePossibleMatchingEvents(data): void {
    if (data.events) {
      this.possibleMatchingEvents = data.events;
    }
  }

  // ----------- EVENTS -----------

  // ----------- GEOGRAPHICAL REVENUE -----------

  resetGeographicalRevenueSearch(): void {
    this.selectedGeographicalRevenueCountries =
      this.countriesOfGeographicalRevenueOptions.map(
        option => (option.shouldShow = true)
      );
    this.selectedGeographicalRevenueTerritories =
      this.territoriesOfGeographicalRevenueOptions.map(
        option => (option.shouldShow = true)
      );
  }

  searchGeographicalRevenue(): void {
    if (
      this.geographicalRevenueTextSearch &&
      this.geographicalRevenueTextSearch.length > 2
    ) {
      this.countriesOfGeographicalRevenueOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));
      this.countriesOfGeographicalRevenueOptions
        .filter(
          option =>
            option.name
              .toLowerCase()
              .indexOf(this.geographicalRevenueTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));

      this.territoriesOfGeographicalRevenueOptions
        .filter(option => (option.shouldShow = true))
        .map(option => (option.shouldShow = false));
      this.territoriesOfGeographicalRevenueOptions
        .filter(
          option =>
            option.name
              .toLowerCase()
              .indexOf(this.geographicalRevenueTextSearch.toLowerCase()) >= 0
        )
        .map(option => (option.shouldShow = true));
    }
  }

  // ----------- GEOGRAPHICAL REVENUE -----------

  expandOrCollapseSidebar(): void {
    this.isSidebarCollapsed.update(value => !value);
    $('.collapse').collapse('hide');
    this.updateScrollBarDimensions();
  }

  expandSideBarIfRequired(): void {
    if (this.isSidebarCollapsed) {
      this.isSidebarCollapsed.set(false);
      this.updateScrollBarDimensions();
    }
  }

  addOrRemoveColumnToWrappedColumnNumbers(columnNumber: number): void {
    if (columnNumber === null) {
      /* null means wrap all columns */
      this.isWrapAllColumns = !this.isWrapAllColumns;
      if (this.isWrapAllColumns) {
        localStorage.setItem(
          'zintAllColumnsWrapped',
          JSON.stringify(this.isWrapAllColumns)
        );
      } else {
        localStorage.removeItem('zintAllColumnsWrapped');
      }

      return;
    }

    if (columnNumber) {
      const index = this.wrappedTableColumnNumbers.indexOf(columnNumber);
      if (index >= 0) {
        this.wrappedTableColumnNumbers.splice(index, 1);
      } else {
        this.wrappedTableColumnNumbers.push(columnNumber);
      }

      localStorage.setItem(
        'zintWrappedColumns',
        JSON.stringify(this.wrappedTableColumnNumbers)
      );
    }
  }

  columnResized($event, columnName): void {
    const width = parseInt($event.target.style.width.replace('px', ''));
    const exisitingValue = this.columnNamesToWidths[columnName];
    // if it's the same then ignore it.
    if (
      (exisitingValue != width || exisitingValue == undefined) &&
      !isNaN(width)
    ) {
      // still resize without waiting for the subscription.
      this.columnNamesToWidths[columnName] = width;

      this.dashboardService
        .saveColumnSize(columnName, width)
        .subscribe(data => {
          if (data.success) {
            this.updateScrollBarDimensions();
          }
        });
    }
  }

  mouseHasLeftHeaderCell($event, columnName): void {
    // Only trigger the column resize event if we are currently clicked on a header cell.
    // We don't want to trigger this if we are just moving the mouse over it.
    if (this.tableHeaderColumnNumberCurrentlyClicked != null) {
      this.tableHeaderColumnNumberCurrentlyClicked = null;
      this.columnResized($event, columnName);
      $($event.target).mouseup();
    }
  }

  showEditColumnsModal(): void {
    $('#editColumnsModal').modal('show');
  }

  toggleDownloadModal(): void {
    this.showDownloadModal = true;
    this.cdr.detectChanges();

    $('#downloadModal').modal();

    $('#downloadModal').on('hide.bs.modal', e => {
      this.showDownloadModal = false;
    });
  }

  toggleMagicBulkUnlockModal(): void {
    this.cdr.detectChanges();

    $('#magicInsightsBulkUnlock').modal('show');

    $('#magicInsightsBulkUnlock').on('hidden.bs.modal', e => {
      this.checkboxSelectAllCompanies = false;
      this.showOrHideInfoOnAllSelectedCompanies();
      this.dataStore.setMagicInsightsUnlockCount(null);
      this.cdr.markForCheck();
    });
  }

  toggleMagicInsightsGenerationModal(): void {
    this.cdr.detectChanges();
    $('#magicInsightsGenerationModal').modal('show');
    $('#magicInsightsGenerationModal').on('hidden.bs.modal', e => {
      this.checkboxSelectAllCompanies = false;
      this.showOrHideInfoOnAllSelectedCompanies();
      this.cdr.markForCheck();
    });
  }

  resetSearchForFilters(inputField?: HTMLInputElement) {
    const hiddenElements = document.querySelectorAll('.d-none');
    if (inputField) {
      inputField.value = '';
    }
    this.filterSearchTerm = '';
    this.filterSearchErrorMsg = '';
    hiddenElements.forEach(el => el.classList.remove('d-none'));
  }

  showHideFilters(searchTerm) {
    const trimmedTerm = searchTerm.trim()?.toLowerCase();
    if (trimmedTerm.length < 2) {
      this.resetSearchForFilters();
      return;
    }

    const trimmedTermsArr = trimmedTerm
      .split(' ')
      .map(keyword => keyword.trim());

    const filtersElementsToShow = Object.entries(this.dynamicFilterIdToKeywordsMappings)
      .filter(([_, keywords]) => {
        return trimmedTermsArr.some((term: string) => keywords.includes(term));
      })
      .map(([id]) => document.querySelector('#' + id));

    if (filtersElementsToShow.length) {
      this.filterSearchTerm = searchTerm;
      this.filterSearchErrorMsg = '';
      this.hideSearchableFilters();

      filtersElementsToShow.forEach(element => {
        this.revealElement(element);
      });
    } else {
      this.filterSearchErrorMsg = 'no match found';
    }
  }

  hideSearchableFilters() {
    /**
     * lv1 - top categories - firmographics, operations etc with ids ending starting with searchable-show-hide
     * the same goes for the nested <details> so their ids start with searchable-show-hide
     * lv2 - collapses - top level collapses with ids ending in -collapse-card
     * bottom level filters - all individual sliders and other filters with ids ending in -filter
     */

    const levelOneElements = document.querySelectorAll(
      '[id^="searchable-show-hide-"]'
    );
    levelOneElements.forEach(el => el.classList.add('d-none'));

    const levelTwoElements = document.querySelectorAll(
      '[id$="-collapse-card"]'
    );
    levelTwoElements.forEach(el => el.classList.add('d-none'));

    const allFiltersWithId = document.querySelectorAll('[id$="-filter"]');

    allFiltersWithId.forEach(el => el.classList.add('d-none'));
  }

  revealElement(element) {
    if (element) {
      element.classList.remove('d-none');

      // Traverse up the DOM to reveal ancestors
      let parent = element.parentElement;
      while (parent) {
        parent.classList.remove('d-none');
        parent = parent.parentElement;
      }
    }
  }

  isFilterSectionDetailsExpanded(event: Event, section: string): void {
    const detailsElement = event.target as HTMLDetailsElement;

    this.expandedFilterSections[section] = detailsElement.open;
  }

  checkIfTableHoveredAndFixed(
    event: Event,
    tableName: string,
    mouseLeave?: boolean
  ): void {
    if (mouseLeave) {
      this.hoveredOverflowingTables[tableName] = false;
      return;
    }

    const divElement = event.target as HTMLDivElement;
    const isNoHoverApplied = divElement.classList.contains('no-hover');

    /* 'no-hover' class is applied by the directive and if the table has this class,
     return the opposite of this check.
    */
    this.hoveredOverflowingTables[tableName] = !isNoHoverApplied;
  }

  getSliceRange(index: number, size: number): Record<string, number> {
    // this returns the dynamic slice indices for the array slice in the template
    const start = index * size;
    const end = start + size;
    return { start, end };
  }

  getSliderDetails(): void {
    this.dashboardService.getSliderDetails().subscribe(data => {
      this.updateSliderDetails(data);
    });
    this.dashboardService.getAiraFilterConfig().subscribe(data => {
      this.updateAiraFilterDetails(data);
    });
  }

  updateSliderDetails(data): void {
    for (const section of data) {
      const sectionName = section['sectionName'];
      if (sectionName === 'Land Ownership and Development') {
        this.landOwnershipAndDevelopmentSliders.push(section);
      } else {
        this.sliderSections.push(section);
      }
      this.zintSlidersConfigs[sectionName] = section['sliders'];
    }
    this.sliderFieldLabels = Object.values(this.zintSlidersConfigs)
      .flat()
      .map(slider => slider.label);
    this.hasLoadedFlags.hasLoadedBackendSliders.set(true);
  }

  updateAiraFilterDetails(data): void {
    this.rawAiraFiltersData = data;
    this.linkedAiraFilters = {};
    this.magicModuleNamesIds = [];
    this.editableMagicModuleNamesIds = [];
    this.organisationMagicModuleNamesIds = [];
    this.nonEditableMagicModuleNamesIds = [];

    data.forEach((module: any) => {
      const sectionName = module.sectionName;

      // Initialize arrays for this section, overwriting any previous data
      this.zintSlidersConfigs[sectionName] = [];
      this.zintTextInputConfigs[sectionName] = [];
      this.zintMultiSelectConfigs[sectionName] = [];

      this.magicModuleNamesIds.push({
        id: module.id,
        name: module.sectionName,
        isOrganisationModule: module.isOrganisationModule,
        // This config only lists questions that expose a filter, so its length is not the
        // module's question count. Left unset until the endpoint returns a real total.
        questionCount: module.questionCount ?? null,
      });

      if (module.isEditable) {
        this.editableMagicModuleNamesIds.push({
          id: module.id,
          name: module.sectionName,
        });
      }
      else{
        this.nonEditableMagicModuleNamesIds.push({
          id: module.id,
          name: module.sectionName,
        });
      }

      if (module.isOrganisationModule) {
        // If you can view the module then you can also attempt to generate answers (but you must of course have quota assigned to you)
        this.organisationMagicModuleNamesIds.push({
          id: module.id,
          name: module.sectionName,
        });
      }

      // this is for searching for matching magic insights filters.
      const sectionKeywords = this.getKeywords(sectionName);
      const moduleCardId = this.removeSpecialCharsAndSpaces(sectionName) + '-collapse-card';
      this.dynamicFilterIdToKeywordsMappings[moduleCardId] = [...sectionKeywords, 'magic', 'aira'];

      module.questions?.forEach((question: any) => {
        Object.values(question.filters).forEach((filtersGroup: any[]) => {
          filtersGroup.forEach((filter: any) => {
            if (filter.label) {
              const filterId = this.simpleKebabCase(filter.label) + '-filter';
              const filterKeywords = this.getKeywords(filter.label);
              this.dynamicFilterIdToKeywordsMappings[filterId] = [...filterKeywords, ...sectionKeywords, 'magic', 'aira'];
            }
            if (filter.filterType === 'range_slider') {
              this.zintSlidersConfigs[sectionName].push(filter);
            } else if (filter.filterType === 'text_input') {
              this.zintTextInputConfigs[sectionName].push(filter);
            } else if (filter.filterType === 'multi_select') {
              this.zintMultiSelectConfigs[sectionName].push(filter);
            }
          });
        });
      });
    });

    this.sliderFieldLabels = Object.values(this.zintSlidersConfigs)
      .flat()
      .map((slider: any) => slider.label);

    this.groupAiraFiltersByLinkedFieldName();
    this.processAIRAMuliSelectOptions();

    this.hasLoadedFlags.hasLoadedAiraFilters.set(true);
  }

  processAIRAMuliSelectOptions(): void {
    this.rawAiraFiltersData.forEach(module => {
      if (module.questions) {
        module.questions.forEach(question => {
          Object.values(question.filters).forEach((filtersGroup: any[]) => {
            filtersGroup.forEach(filter => {
              if (
                filter.filterType === 'multi_select' &&
                filter.options_type &&
                filter.options_type === 'countries_and_territories'
              ) {
                // we have to make a deep copy of the object from file.
                filter.options = JSON.parse(
                  JSON.stringify(this.magicCountriesList)
                );
              }
            });
          });
        });
      }
    });
  }

  groupAiraFiltersByLinkedFieldName() {
    this.rawAiraFiltersData.forEach(section => {
      const sectionName = section.sectionName;
      // Initialise the section in linkedAiraFilters if not present.
      if (!this.linkedAiraFilters[sectionName]) {
        this.linkedAiraFilters[sectionName] = {
          id: section.id,
          hasFilterAccess: section.hasFilterAccess || false,
          isEditable: section.isEditable || false,
          newQuestion: '',
          answerCount: section.answerCount,
          lastAnswerGeneratedAt: section.lastAnswerGeneratedAt,
        };
      }

      if (section.questions) {
        section.questions.forEach(question => {
          Object.keys(question.filters).forEach(linkedFieldKey => {
            if (!this.linkedAiraFilters[sectionName][linkedFieldKey]) {
              this.linkedAiraFilters[sectionName][linkedFieldKey] = [];
            }
            this.linkedAiraFilters[sectionName][linkedFieldKey].push(
              ...question.filters[linkedFieldKey]
            );
          });
        });
      }
    });
    this.setHasAIRAModulesEnabledOptions();
  }

  setHasAIRAModulesEnabledOptions(): void {
    this.selectedModules = [];
    Object.keys(this.linkedAiraFilters).forEach(key => {
      this.selectedModules.push({
        id: this.linkedAiraFilters[key].id,
        name: key,
        checked: false,
      });
    });
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (!this.isScreenTooSmall()) {
      return;
    } else {
      const sidebar = document.querySelector('.sidebar-grid') as HTMLDivElement;
      const sidebarTop = sidebar.getBoundingClientRect().top;

      if (sidebarTop <= 18) {
        this.mobileSidebarBtnFixedTop = true;
      } else {
        this.mobileSidebarBtnFixedTop = false;
      }
    }
  }

  toggleSidebarHeight(resetting = false) {
    const sidebar = document.querySelector('.sidebar-grid') as HTMLDivElement;
    if (resetting) {
      sidebar.style.height = '100dvh';
    }

    if (!sidebar.style.height) {
      sidebar.style.height = '0.1px';
      this.mobileSidebarRolledUp = true;
    } else {
      sidebar.style.height = '';
      this.mobileSidebarRolledUp = false;
    }
  }

  createAiraModuleQuestion(section: any): void {
    const questionText = section.value.newQuestion;
    if (!questionText || questionText.trim().length < 3) {
      this.pubMethods.showWarningMessageWithoutAutoDisappear(
        'Please enter a valid question.'
      );
      return;
    }
    this.dashboardService
      .createAiraModuleQuestion(section.value.id, questionText)
      .subscribe(data => {
        if (data.id) {
          this.dashboardService.getAiraFilterConfig().subscribe(newData => {
            this.updateAiraFilterDetails(newData);
          });
          this.pubMethods.showInfoMessage('New question created!');
          section.value.newQuestion = '';
        }
      });
  }

  removeSpecialCharsAndSpaces(str: string): string {
    // Removes spaces AND special characters like &, %, $, etc.
    // Keeps letters, numbers, hyphens, and underscores.
    return str.replace(/[^a-zA-Z0-9-_]/g, '');
  }

  private simpleKebabCase(text: string): string {
    if (!text) return '';
    return text
      .replace(/([a-z])([A-Z])/g, '$1-$2') // Handle camelCase
      .replace(/[\s_]+/g, '-') // Replace spaces and underscores
      .toLowerCase();
  }

  private getKeywords(text: string): string[] {
    if (!text) return [];
    // Split by space, underscore, or hyphen
    return text.toLowerCase().split(/[\s_-]+/);
  }

  filterTags(): void {
    const trimmedTerm = this.tagSearchTerm.trim().toLowerCase();
    const filterList = (tag) => {
      if (trimmedTerm.length < 2) {
        tag.shouldShow = true;
      } else {
        // Check if tag name exists before calling toLowerCase
        tag.shouldShow = tag.name && tag.name.toLowerCase().includes(trimmedTerm);
      }
      return tag;
    };

    this.tagsPositiveOptions = this.tagsPositiveOptions.map(filterList);
    this.tagsNegativeOptions = this.tagsNegativeOptions.map(filterList);
  }


  updateFilteredCustomColumns(): void {
    if (!this.customColumns) {
      this.filteredCustomColumns = [];
      return;
    }

    const trimmedTerm = this.customColumnSearchTerm.trim().toLowerCase();

    this.filteredCustomColumns = this.customColumns.filter(col => {
      if (trimmedTerm.length < 2) {
        return true; // Show all columns if search is empty
      }

      // Filter by name
      return col.name && col.name.toLowerCase().includes(trimmedTerm);
    });
  }

  filterCustomColumns(): void {
    this.updateFilteredCustomColumns();
  }




}
